import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowLeft, BookOpen, Copy, Globe, Bell, Bot, Check, ChevronDown, ChevronRight, Clock, Coins, Cpu, Crown, DoorClosed, DoorOpen, Eye, Folder, Gavel, Hash, ImagePlus, KeyRound, Link2,
  Loader2, Megaphone, MessagesSquare, Pencil, MessageSquare, Palette, Plus, Power, Radio, RefreshCw, RotateCw, Save, ScrollText, Search, Send, Server, Settings2, Shield, ShieldAlert, Smile,
  LayoutDashboard, Sparkles, Tags, Terminal, Ticket, Trash2, Undo2, Upload, UserCog, UserX, Users, Volume2, Wand2, X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import PanelShell from "@/components/PanelShell";
import DiscordFormatBar from "@/components/DiscordFormatBar";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";
import TokenSwitchStatus from "@/components/TokenSwitchStatus";

// Tableau de bord des bots de gestion : chaque serveur Discord du bot se règle
// ici. Le bot récupère les changements toutes les ~20 s et les applique.

const auth = () => ({ headers: { Authorization: `Bearer ${tokenStore.get()}` } });
const dash = {
  get: (path) => axios.get(`${API}/dash${path}`, auth()).then((r) => r.data),
  put: (path, body) => axios.put(`${API}/dash${path}`, body, auth()).then((r) => r.data),
  post: (path, body) => axios.post(`${API}/dash${path}`, body || {}, auth()).then((r) => r.data),
  del: (path) => axios.delete(`${API}/dash${path}`, auth()).then((r) => r.data),
};
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const clone = (v) => JSON.parse(JSON.stringify(v));

const ROLE_INFO = {
  acheteur: { label: "Acheteur", icon: Crown, cls: "border-amber-500/40 bg-amber-500/10 text-amber-200", rights: "Tu as acheté ce bot : tu règles tout et tu gères les buyers et les owners." },
  buyer: { label: "Buyer", icon: Sparkles, cls: "border-violet-500/40 bg-violet-500/10 text-violet-200", rights: "Tu règles tout et tu gères les owners. Seul l'acheteur ajoute des buyers." },
  owner: { label: "Owner", icon: Shield, cls: "border-sky-500/40 bg-sky-500/10 text-sky-200", rights: "Tu vois tous les réglages et tu règles la sécurité (antiraid). Le reste est réservé aux buyers, comme dans le bot." },
};

const TABS = [
  { id: "home", label: "Accueil", hint: "Vue d'ensemble du serveur", icon: LayoutDashboard },
  { id: "general", label: "Général", hint: "Couleur des messages", icon: Palette },
  { id: "join", label: "Arrivées", hint: "Message, embed, rôles", icon: DoorOpen },
  { id: "leave", label: "Départs", hint: "Message de départ", icon: DoorClosed },
  { id: "security", label: "Sécurité", hint: "Antiraid et sanctions", icon: ShieldAlert },
  { id: "automod", label: "Modération auto", hint: "Anti-spam, mots bannis", icon: Shield },
  { id: "netban", label: "Bans réseau", hint: "Bannis du réseau VantaPulse", icon: Globe },
  { id: "logs", label: "Logs", hint: "Journal du serveur et salons de logs", icon: ScrollText },
  { id: "leveling", label: "Niveaux", hint: "XP et rôles de niveau", icon: Sparkles },
  { id: "notifications", label: "Notifications", hint: "YouTube, Twitch, Kick, RSS", icon: Bell },
  { id: "ai", label: "Créateur IA", hint: "Créer le serveur avec l'IA", icon: Wand2 },
  { id: "embeds", label: "Messages & embeds", hint: "Envoyer dans un salon", icon: MessageSquare },
  { id: "tickets", label: "Tickets", hint: "Panel et types de ticket", icon: Ticket },
  { id: "rolemenus", label: "Menus de rôles", hint: "Boutons et menus qui donnent des rôles", icon: Tags },
  { id: "autoreact", label: "Réactions auto", hint: "Réactions par salon", icon: Smile },
  { id: "bot", label: "Bot", hint: "Préfixe, statut, activité", icon: Bot },
  { id: "commands", label: "Commandes", hint: "Toutes les commandes et leur aide", icon: BookOpen },
  { id: "team", label: "Équipe", hint: "Buyers et owners", icon: UserCog },
  { id: "system", label: "Système", hint: "Statut et relance du bot", icon: Cpu },
];
// Bots radio : leurs propres onglets.
const RADIO_TABS = [
  { id: "radio", label: "Salons radio", hint: "Une radio par salon vocal", icon: Radio },
  { id: "bot", label: "Bot", hint: "Préfixe, statut, activité", icon: Bot },
  { id: "team", label: "Équipe", hint: "Buyers et owners", icon: UserCog },
  { id: "system", label: "Système", hint: "Statut et relance du bot", icon: Cpu },
];
// Onglets qui ne sont pas des réglages du serveur (actions, réglages du bot, équipe, système).
const NON_SECTION_TABS = ["home", "ai", "embeds", "bot", "commands", "team", "system"];
const SECTION_LABELS = Object.fromEntries([...TABS, ...RADIO_TABS].map((t) => [t.id, t.label]));

const PLACEHOLDERS = [
  ["{user}", "mention du membre"], ["{username}", "pseudo"], ["{server}", "nom du serveur"], ["{members}", "nombre de membres"],
  ["{inviter}", "qui l'a invité"], ["{invites}", "ses invitations"], ["{code}", "code d'invitation"],
];
const PLATFORM_LABELS = { youtube: "YouTube", twitch: "Twitch", kick: "Kick", rss: "X / Twitter ou flux RSS" };
const KIND_LABELS = { all: "Tout (vidéos, shorts, lives)", video: "Vidéos", short: "Shorts", live: "Lives", post: "Nouveaux posts" };
const LINK_HELP = {
  youtube: "Lien de la chaîne ou @nom (ex. https://youtube.com/@machaine)",
  twitch: "Lien ou nom de la chaîne Twitch",
  kick: "Lien ou nom de la chaîne Kick",
  rss: "Adresse du flux RSS (pour X/Twitter : flux créé avec rss.app par exemple)",
};
const LEVEL_LABELS = { off: "Désactivé", on: "Activé", max: "Maximum" };
const SANCTION_LABELS = { derank: "Retirer les rôles", kick: "Expulser", ban: "Bannir" };

const since = (iso) => {
  if (!iso) return "jamais";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `il y a ${Math.floor(s)} s`;
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
};

// ── Petits composants ──

function Panel({ title, icon: Icon, actions, children, className = "" }) {
  return (
    <section className={`bg-[#0f1019] border border-white/[0.07] rounded-2xl ${className}`}>
      {title && (
        <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-white/[0.06]">
          <h2 className="font-heading font-semibold text-sm flex items-center gap-2">{Icon && <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/10 ring-1 ring-inset ring-blue-400/15"><Icon className="w-3.5 h-3.5 text-blue-300" /></span>}{title}</h2>
          {actions}
        </header>
      )}
      <div className="p-4 space-y-4">{children}</div>
    </section>
  );
}

function Btn({ children, onClick, variant = "ghost", disabled, className = "", title, type = "button", testId, ...rest }) {
  const styles = {
    primary: "bg-blue-600 hover:bg-blue-500 text-white border-blue-500",
    ghost: "bg-white/5 hover:bg-white/10 text-white border-white/10",
    danger: "bg-red-500/10 hover:bg-red-500/20 text-red-200 border-red-500/30",
  };
  return (
    <button type={type} title={title} onClick={onClick} disabled={disabled} data-testid={testId} {...rest}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

function Toggle({ checked, onChange, label, disabled, testId }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled} data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-white/15"} disabled:opacity-40 disabled:cursor-not-allowed`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function Row({ label, help, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <div className="sm:w-64 shrink-0">
        <div className="text-sm">{label}</div>
        {help && <div className="text-xs text-text-muted">{help}</div>}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function SwitchRow({ label, help, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm">{label}</div>
        {help && <div className="text-xs text-text-muted">{help}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} disabled={disabled} testId={testId} />
    </div>
  );
}

const inputCls = "w-full bg-[#0c0c14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-text-muted focus:outline-none focus:border-blue-500 disabled:opacity-50";

function TextInput({ value, onChange, disabled, placeholder, maxLength, testId, type = "text" }) {
  return <input type={type} className={inputCls} value={value ?? ""} disabled={disabled} placeholder={placeholder} maxLength={maxLength}
    data-testid={testId} onChange={(e) => onChange(e.target.value)} />;
}

// Image facultative : fichier envoyé depuis l'ordinateur (stocké sur le site), lien
// (avec ou sans https://) ou rien.
function ImageField({ value, onChange, disabled, placeholder = "(facultatif)", testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type)) { toast.error("Image PNG, JPEG, GIF ou WebP"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (5 Mo maximum)"); return; }
    setBusy(true);
    try {
      const data = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
      const res = await axios.post(`${API}/upload/image`, { image: data }, auth());
      onChange(res.data.url);
      toast.success("Image envoyée");
    } catch (err) { toast.error(errorText(err, "Envoi impossible")); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" ref={inputRef} data-testid={testId ? `${testId}-file` : undefined}
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        <Btn onClick={() => inputRef.current?.click()} disabled={disabled || busy} testId={testId ? `${testId}-upload` : undefined} className="shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Envoyer une image
        </Btn>
        <input className={inputCls} value={value ?? ""} disabled={disabled} placeholder={`ou colle un lien ${placeholder}`} data-testid={testId}
          onChange={(e) => onChange(e.target.value)} />
        {value && !disabled && <Btn onClick={() => onChange("")} title="Retirer l'image"><X className="w-4 h-4" /></Btn>}
      </div>
      {value && <img src={/^(https?:|\/|data:)/i.test(value) ? value : `https://${value}`} alt="" className="max-h-28 rounded border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} onLoad={(e) => { e.currentTarget.style.display = ""; }} />}
    </div>
  );
}

function TextArea({ value, onChange, disabled, placeholder, maxLength = 1500, rows = 3, testId }) {
  const ref = useRef(null);
  return (
    <div className="relative">
      <textarea ref={ref} className={`${inputCls} resize-y`} rows={rows} value={value ?? ""} disabled={disabled} placeholder={placeholder}
        maxLength={maxLength} data-testid={testId} onChange={(e) => onChange(e.target.value)} />
      <DiscordFormatBar inputRef={ref} value={value} onChange={onChange} disabled={disabled} maxLength={maxLength} />
      <div className="text-[11px] text-text-muted text-right">{(value || "").length}/{maxLength}</div>
    </div>
  );
}

function NumberInput({ value, onChange, disabled, min, max, testId, suffix }) {
  return (
    <div className="flex items-center gap-2">
      <input type="number" className={`${inputCls} max-w-[140px]`} value={value ?? 0} min={min} max={max} disabled={disabled} data-testid={testId}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Math.round(Number(e.target.value)))} />
      {suffix && <span className="text-xs text-text-muted">{suffix}</span>}
    </div>
  );
}

function ColorInput({ value, onChange, disabled, allowEmpty, testId }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" className="w-10 h-9 bg-transparent border border-white/10 disabled:opacity-50" value={value || "#7c3aed"} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} aria-label="Couleur" />
      <input className={`${inputCls} max-w-[140px] font-mono`} value={value || ""} disabled={disabled} maxLength={7} placeholder={allowEmpty ? "par défaut" : "#5865f2"}
        data-testid={testId} onChange={(e) => onChange(e.target.value.trim())} />
      {allowEmpty && value && !disabled && <Btn onClick={() => onChange("")} title="Couleur par défaut du bot"><Undo2 className="w-3.5 h-3.5" /></Btn>}
    </div>
  );
}

function Select({ value, onChange, disabled, options, testId }) {
  return <Picker value={value ?? ""} options={options} onChange={onChange} disabled={disabled} testId={testId} />;
}

// ── Sélecteur façon Discord : recherche, catégories, icônes, clavier ──

const CHANNEL_ICON = { 0: Hash, 5: Megaphone, 2: Volume2, 13: Radio, 15: MessagesSquare, 4: Folder };
const channelIcon = (c) => CHANNEL_ICON[c?.type] || Hash;
const roleColor = (r) => (r && r.color && r.color !== "#000000" ? r.color : "#99aab5");

// options : [{ value, label, group, icon, color, disabled, hint }]
function Picker({ value, options, onChange, disabled, placeholder = "Choisir…", testId, empty, searchPlaceholder = "Rechercher", trigger }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  const all = useMemo(() => (empty ? [{ value: "", label: empty, empty: true }, ...options] : options), [options, empty]);
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? all.filter((o) => !o.empty && `${o.label} ${o.group || ""} ${o.hint || ""}`.toLowerCase().includes(n)) : all;
  }, [all, q]);
  const current = all.find((o) => o.value === (value || "")) || null;
  const searchable = all.length > 8;
  const place = useCallback(() => {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    const up = below < 300 && r.top > below;
    setPos({ left: Math.min(r.left, window.innerWidth - Math.max(r.width, 260) - 8), width: Math.max(r.width, 260), top: up ? undefined : r.bottom + 6, bottom: up ? window.innerHeight - r.top + 6 : undefined, max: Math.min(340, (up ? r.top : below) - 16) });
  }, []);
  const openIt = () => { if (disabled) return; place(); setQ(""); setHi(Math.max(0, shown.findIndex((o) => o.value === (value || "")))); setOpen(true); };
  const choose = (o) => { if (!o || o.disabled) return; onChange(o.value); setOpen(false); btn.current?.focus(); };
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!pop.current?.contains(e.target) && !btn.current?.contains(e.target)) setOpen(false); };
    const move = (e) => { if (!pop.current?.contains(e.target)) place(); };
    document.addEventListener("mousedown", away);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", move, true);
    return () => { document.removeEventListener("mousedown", away); window.removeEventListener("resize", place); window.removeEventListener("scroll", move, true); };
  }, [open, place]);
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => { pop.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: "nearest" }); }, [hi, open]);
  const key = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(shown.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(shown[hi]); }
    else if (e.key === "Escape") { setOpen(false); btn.current?.focus(); }
  };
  const Icon = current?.icon;
  let lastGroup = null;
  return (
    <>
      {trigger ? (
        <button type="button" ref={btn} disabled={disabled} onClick={() => (open ? setOpen(false) : openIt())} data-testid={testId}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-dashed border-white/15 text-text-muted hover:text-white hover:border-white/30 disabled:opacity-40">{trigger}</button>
      ) : (
        <button type="button" ref={btn} disabled={disabled} onClick={() => (open ? setOpen(false) : openIt())} data-testid={testId} aria-haspopup="listbox" aria-expanded={open}
          className={`${inputCls} flex items-center gap-2 text-left ${open ? "border-blue-500" : ""}`}>
          {Icon && <Icon className="w-4 h-4 shrink-0 text-text-muted" />}
          {current?.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: current.color }} />}
          <span className={`flex-1 truncate ${current && !current.empty ? "text-white" : "text-text-muted"}`}>{current ? current.label : value ? `Introuvable (${value})` : placeholder}</span>
          {current?.group && <span className="hidden sm:block text-[11px] text-text-muted truncate max-w-[40%]">{current.group}</span>}
          <ChevronDown className={`w-4 h-4 shrink-0 text-text-muted transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && pos && createPortal(
        <div ref={pop} role="listbox" data-testid={testId ? `${testId}-menu` : undefined}
          className="fixed z-[200] rounded-xl border border-white/10 bg-[#111119] shadow-2xl shadow-black/70 overflow-hidden"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}>
          {searchable ? (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
              <Search className="w-4 h-4 text-text-muted shrink-0" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={key} placeholder={searchPlaceholder}
                className="flex-1 bg-transparent text-sm text-white placeholder:text-text-muted focus:outline-none" data-testid={testId ? `${testId}-search` : undefined} />
            </div>
          ) : <input autoFocus readOnly onKeyDown={key} className="absolute w-0 h-0 opacity-0" aria-hidden="true" />}
          <div className="overflow-y-auto py-1" style={{ maxHeight: Math.max(160, pos.max - 44) }}>
            {shown.length === 0 && <div className="px-3 py-6 text-center text-xs text-text-muted">Aucun résultat</div>}
            {shown.map((o, idx) => {
              const header = o.group && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group || lastGroup;
              const OIcon = o.icon;
              const selected = o.value === (value || "");
              return (
                <Fragment key={`${o.value}-${idx}`}>
                  {header && <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted truncate">{header}</div>}
                  <button type="button" data-idx={idx} disabled={o.disabled} onMouseEnter={() => setHi(idx)} onClick={() => choose(o)} role="option" aria-selected={selected}
                    data-value={o.value}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${idx === hi ? "bg-blue-500/15 text-white" : "text-white/80"} ${o.disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                    {OIcon ? <OIcon className="w-4 h-4 shrink-0 text-text-muted" /> : o.color ? <span className="w-2.5 h-2.5 rounded-full shrink-0 mx-[3px]" style={{ background: o.color }} /> : <span className="w-4" />}
                    <span className={`flex-1 truncate ${o.empty ? "text-text-muted italic" : ""}`}>{o.label}</span>
                    {o.hint && <span className="text-[11px] text-text-muted truncate max-w-[45%]">{o.hint}</span>}
                    {selected && <Check className="w-4 h-4 shrink-0 text-blue-400" />}
                  </button>
                </Fragment>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

const channelOptions = (list) => list.map((c) => ({ value: c.id, label: c.name, group: c.category || "Sans catégorie", icon: channelIcon(c) }));

function ChannelSelect({ value, onChange, disabled, channels, emptyLabel = "Aucun", testId, allowEmpty = true }) {
  const text = useMemo(() => channels.filter((c) => c.type === 0 || c.type === 5), [channels]);
  const options = useMemo(() => channelOptions(text), [text]);
  if (!channels.length) {
    // Discord n'a pas répondu : identifiant du salon à la main.
    return <TextInput value={value} onChange={onChange} disabled={disabled} placeholder="ID du salon" testId={testId} />;
  }
  return <Picker value={value} options={options} onChange={onChange} disabled={disabled} testId={testId} empty={allowEmpty || !value ? emptyLabel : null}
    placeholder={emptyLabel} searchPlaceholder="Rechercher un salon" />;
}

const assignable = (roles) => roles.filter((r) => !r.managed && !r.everyone);

function RoleChip({ role, id, onRemove, disabled }) {
  const color = roleColor(role);
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-md bg-white/5 border border-white/10">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      {role ? role.name : `Rôle supprimé (${id})`}
      {!disabled && <button type="button" onClick={onRemove} aria-label="Retirer" className="text-text-muted hover:text-white"><X className="w-3 h-3" /></button>}
    </span>
  );
}

function RolePicker({ value, onChange, disabled, roles, max = 10, testId }) {
  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));
  const options = assignable(roles).filter((r) => !value.includes(r.id));
  const [manual, setManual] = useState("");
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 && <span className="text-xs text-text-muted">Aucun rôle</span>}
        {value.map((id) => <RoleChip key={id} id={id} role={byId[id]} disabled={disabled} onRemove={() => onChange(value.filter((v) => v !== id))} />)}
      </div>
      {!disabled && value.length < max && (roles.length ? (
        <Picker value="" options={options.map((r) => ({ value: r.id, label: r.name, color: roleColor(r) }))} onChange={(v) => v && onChange([...value, v])}
          testId={testId} trigger={<><Plus className="w-3.5 h-3.5" />Ajouter un rôle</>} searchPlaceholder="Rechercher un rôle" />
      ) : (
        <div className="flex gap-2">
          <TextInput value={manual} onChange={setManual} placeholder="ID du rôle" />
          <Btn onClick={() => { if (/^\d{15,25}$/.test(manual)) { onChange([...value, manual]); setManual(""); } }}><Plus className="w-4 h-4" /></Btn>
        </div>
      ))}
    </div>
  );
}

function Placeholders() {
  return (
    <div className="flex flex-wrap gap-1.5 text-[11px]">
      {PLACEHOLDERS.map(([k, l]) => <span key={k} className="px-1.5 py-0.5 bg-white/5 border border-white/10" title={l}><code className="text-blue-300">{k}</code> {l}</span>)}
    </div>
  );
}

function SectionStatus({ pending, error, online }) {
  if (error) {
    return <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-100" data-testid="section-error"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Le bot n'a pas pu tout appliquer : {error}</div>;
  }
  if (pending) {
    return (
      <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-100" data-testid="section-pending">
        {online ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
        {online ? "Enregistré : le bot applique ce changement (quelques secondes)…" : "Enregistré : sera appliqué dès que le bot sera reconnecté au site."}
      </div>
    );
  }
  return null;
}

// ── Onglets de réglages ──

function GeneralTab({ s, set, ro }) {
  return (
    <Panel title="Apparence" icon={Palette}>
      <Row label="Couleur des embeds" help="Couleur des messages du bot sur ce serveur (+color). Vide : couleur du bot.">
        <ColorInput value={s.color} onChange={(v) => set({ ...s, color: v })} disabled={ro} allowEmpty testId="general-color" />
      </Row>
    </Panel>
  );
}

function JoinTab({ s, set, ro, channels, roles }) {
  const e = s.embed;
  const setEmbed = (patch) => set({ ...s, embed: { ...e, ...patch } });
  return (
    <div className="space-y-4">
      <Panel title="Message d'arrivée" icon={DoorOpen}>
        <SwitchRow label="Messages d'arrivée" help="Un message quand quelqu'un rejoint le serveur" checked={s.enabled} onChange={(v) => set({ ...s, enabled: v })} disabled={ro} testId="join-enabled" />
        <Row label="Salon" help="Vide : salon système du serveur">
          <ChannelSelect value={s.channel} onChange={(v) => set({ ...s, channel: v })} disabled={ro || !s.enabled} channels={channels} emptyLabel="Salon système du serveur" testId="join-channel" />
        </Row>
        <Row label="Message (invité par quelqu'un)"><TextArea value={s.messages.default} onChange={(v) => set({ ...s, messages: { ...s.messages, default: v } })} disabled={ro} testId="join-msg-default" /></Row>
        <Row label="Message (lien personnalisé)"><TextArea value={s.messages.vanity} onChange={(v) => set({ ...s, messages: { ...s.messages, vanity: v } })} disabled={ro} /></Row>
        <Row label="Message (invitation inconnue)"><TextArea value={s.messages.unknown} onChange={(v) => set({ ...s, messages: { ...s.messages, unknown: v } })} disabled={ro} /></Row>
        <Placeholders />
      </Panel>
      <Panel title="Embed d'arrivée" icon={Sparkles}>
        <SwitchRow label="Envoyer en embed" help="Encadré avec titre, couleur, avatar et bannière" checked={e.enabled} onChange={(v) => setEmbed({ enabled: v })} disabled={ro} testId="join-embed" />
        {e.enabled && (
          <>
            <Row label="Titre"><TextInput value={e.title} onChange={(v) => setEmbed({ title: v })} disabled={ro} maxLength={256} /></Row>
            <Row label="Description"><TextArea value={e.description} onChange={(v) => setEmbed({ description: v })} disabled={ro} maxLength={2000} /></Row>
            <Row label="Couleur"><ColorInput value={e.color} onChange={(v) => setEmbed({ color: v })} disabled={ro} /></Row>
            <Row label="Bannière" help="Facultatif : envoie une image, colle un lien, ou laisse vide"><ImageField value={e.banner} onChange={(v) => setEmbed({ banner: v })} disabled={ro} /></Row>
            <Row label="Pied de l'embed"><TextInput value={e.footer} onChange={(v) => setEmbed({ footer: v })} disabled={ro} maxLength={256} /></Row>
            <SwitchRow label="Avatar du membre" checked={e.thumbnail} onChange={(v) => setEmbed({ thumbnail: v })} disabled={ro} />
            <SwitchRow label="Afficher « invité par »" checked={e.invitedBy} onChange={(v) => setEmbed({ invitedBy: v })} disabled={ro} />
          </>
        )}
      </Panel>
      <Panel title="Rôles et message privé" icon={Users}>
        <Row label="Rôles donnés à l'arrivée" help="10 maximum (+joinrole)"><RolePicker value={s.roles} onChange={(v) => set({ ...s, roles: v })} disabled={ro} roles={roles} testId="join-roles" /></Row>
        <Row label="Message privé de bienvenue" help="Vide : pas de message privé"><TextArea value={s.dm} onChange={(v) => set({ ...s, dm: v })} disabled={ro} placeholder="Bienvenue {user} sur {server} !" /></Row>
      </Panel>
    </div>
  );
}

function LeaveTab({ s, set, ro, channels }) {
  const e = s.embed;
  const setEmbed = (patch) => set({ ...s, embed: { ...e, ...patch } });
  return (
    <Panel title="Messages de départ" icon={DoorClosed}>
      <SwitchRow label="Messages de départ" help="Désactivés par défaut" checked={s.enabled} onChange={(v) => set({ ...s, enabled: v })} disabled={ro} testId="leave-enabled" />
      <Row label="Salon" help="Vide : salon système du serveur">
        <ChannelSelect value={s.channel} onChange={(v) => set({ ...s, channel: v })} disabled={ro || !s.enabled} channels={channels} emptyLabel="Salon système du serveur" testId="leave-channel" />
      </Row>
      <Row label="Message (invité par quelqu'un)"><TextArea value={s.messages.default} onChange={(v) => set({ ...s, messages: { ...s.messages, default: v } })} disabled={ro} /></Row>
      <Row label="Message (invitation inconnue)"><TextArea value={s.messages.unknown} onChange={(v) => set({ ...s, messages: { ...s.messages, unknown: v } })} disabled={ro} /></Row>
      <Placeholders />
      <SwitchRow label="Envoyer en embed" checked={e.enabled} onChange={(v) => setEmbed({ enabled: v })} disabled={ro} />
      {e.enabled && (
        <>
          <Row label="Titre"><TextInput value={e.title} onChange={(v) => setEmbed({ title: v })} disabled={ro} maxLength={256} /></Row>
          <Row label="Description" help="Vide : le message ci-dessus"><TextArea value={e.description} onChange={(v) => setEmbed({ description: v })} disabled={ro} maxLength={2000} /></Row>
          <Row label="Couleur"><ColorInput value={e.color} onChange={(v) => setEmbed({ color: v })} disabled={ro} /></Row>
          <Row label="Pied de l'embed"><TextInput value={e.footer} onChange={(v) => setEmbed({ footer: v })} disabled={ro} maxLength={256} /></Row>
          <SwitchRow label="Avatar du membre" checked={e.thumbnail} onChange={(v) => setEmbed({ thumbnail: v })} disabled={ro} />
        </>
      )}
    </Panel>
  );
}

function SecurityTab({ s, set, ro, meta }) {
  const setAll = (level) => set({ ...s, modules: Object.fromEntries(Object.keys(s.modules).map((k) => [k, k === "antijoin" && level !== "off" ? "off" : level])) });
  return (
    <Panel title="Antiraid" icon={ShieldAlert} actions={!ro && (
      <div className="flex flex-wrap justify-end gap-1.5">
        <Btn onClick={() => setAll("on")} testId="secur-on">Tout activer</Btn>
        <Btn onClick={() => setAll("max")}>Tout au max</Btn>
        <Btn onClick={() => setAll("off")}>Tout couper</Btn>
      </div>
    )}>
      <p className="text-xs text-text-muted">« Activé » : les membres de la whitelist du serveur (+wl) passent. « Maximum » : la whitelist ne protège plus, seuls les owners et buyers du bot passent. L'anti-arrivées n'est à activer qu'en cas de raid.</p>
      <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden">
        {meta.security_modules.map((m) => (
          <div key={m.key} className="flex flex-col md:flex-row md:items-center gap-2 p-3" data-testid={`secur-${m.key}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm">{m.label}</div>
              <div className="text-xs text-text-muted">{m.help}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex border border-white/10">
                {["off", "on", "max"].map((lvl) => (
                  <button key={lvl} type="button" disabled={ro} onClick={() => set({ ...s, modules: { ...s.modules, [m.key]: lvl } })}
                    className={`px-2.5 py-1 text-xs ${s.modules[m.key] === lvl ? (lvl === "off" ? "bg-white/15 text-white" : lvl === "on" ? "bg-blue-600 text-white" : "bg-red-600 text-white") : "text-text-muted hover:text-white"} disabled:cursor-not-allowed`}>
                    {LEVEL_LABELS[lvl]}
                  </button>
                ))}
              </div>
              <div className="sm:w-40"><Picker value={s.sanctions[m.key]} disabled={ro || s.modules[m.key] === "off"} testId={`sanction-${m.key}`}
                onChange={(v) => set({ ...s, sanctions: { ...s.sanctions, [m.key]: v } })} options={Object.entries(SANCTION_LABELS).map(([k, l]) => ({ value: k, label: l }))} /></div>
            </div>
          </div>
        ))}
      </div>
      <Row label="Anti-liens : liens bloqués">
        <Select value={s.antilink_type} onChange={(v) => set({ ...s, antilink_type: v })} disabled={ro}
          options={[{ value: "invites", label: "Invitations Discord seulement" }, { value: "all", label: "Tous les liens" }]} />
      </Row>
    </Panel>
  );
}

// Bans réseau : messages par défaut du bot (champ vide = celui-ci).
const NETBAN_DEFAULTS = {
  message_quarantine: "🚫 {user}, tu es banni du réseau VantaPulse : tu n'as accès qu'à ce salon.\nRaison : {reason}",
  message_kick: "Tu as été exclu de **{server}** : tu es banni du réseau VantaPulse.\nRaison : {reason}",
  message_ban: "Tu as été banni de **{server}** : tu es banni du réseau VantaPulse.\nRaison : {reason}",
};
const NETBAN_ACTIONS = [
  { value: "quarantine", label: "Aucun rôle", help: "Il perd ses rôles et ne voit plus qu'un seul salon (rôles rendus si le ban réseau est levé)." },
  { value: "kick", label: "Exclure", help: "Il reçoit un message privé puis il est exclu (il peut revenir, il sera exclu à nouveau)." },
  { value: "ban", label: "Bannir", help: "Il reçoit un message privé puis il est banni (débanni si le ban réseau est levé)." },
  { value: "alert", label: "Laisser entrer", help: "Personne n'est sanctionné : tu es seulement prévenu quand un banni du réseau arrive." },
];
// Catégories de ban (triées par le site d'après la raison : #hashtag, mots-clés, IA). Gravité 3 grave · 2 moyen · 1 léger.
const NETBAN_CATEGORIES = [
  { id: "triche", label: "Triche / cheat", severity: 3, example: "mod menu, aimbot, Eulen…" },
  { id: "arnaque", label: "Arnaque / vol", severity: 3, example: "scam, faux paiement, leak…" },
  { id: "raid", label: "Raid / nuke", severity: 3, example: "raid, selfbot, DDoS…" },
  { id: "menaces", label: "Menaces / doxx", severity: 3, example: "menaces, doxx, chantage…" },
  { id: "illegal", label: "Contenu illégal / mineurs", severity: 3, example: "pédo, vente illégale…" },
  { id: "harcelement", label: "Harcèlement / haine", severity: 2, example: "harcèlement, racisme…" },
  { id: "toxicite", label: "Insultes / toxicité", severity: 1, example: "insultes, provocation…" },
  { id: "pub", label: "Pub / spam", severity: 1, example: "pub, spam, démarchage…" },
  { id: "autre", label: "Autre", severity: 1, example: "raison non reconnue" },
];
const SEVERITY_BADGE = {
  3: ["Grave", "text-red-300 border-red-500/40 bg-red-500/10"],
  2: ["Moyen", "text-amber-200 border-amber-500/40 bg-amber-500/10"],
  1: ["Léger", "text-white/60 border-white/15 bg-white/5"],
};
const NETBAN_ALERTS = [
  { value: "owner", label: "Fondateur (MP)" },
  { value: "channel", label: "Un salon" },
  { value: "off", label: "Personne" },
];

function NetbanMessage({ s, set, ro, field, label, help }) {
  const custom = !!s[field];
  return (
    <Row label={label} help={help}>
      <div className="space-y-1.5">
        <TextArea value={s[field] || NETBAN_DEFAULTS[field]} onChange={(v) => set({ ...s, [field]: v === NETBAN_DEFAULTS[field] ? "" : v })} disabled={ro} rows={3} testId={`netban-${field}`} />
        <div className="flex items-center gap-2 text-[11px] text-white/40">
          <span>{custom ? "Message personnalisé" : "Message par défaut"}</span>
          {custom && !ro && <button type="button" className="underline hover:text-white" onClick={() => set({ ...s, [field]: "" })}>Remettre par défaut</button>}
        </div>
      </div>
    </Row>
  );
}

function NetbanTab({ s, set, ro, channels }) {
  const action = NETBAN_ACTIONS.find((a) => a.value === s.action) || NETBAN_ACTIONS[0];
  const cats = s.categories || NETBAN_CATEGORIES.map((c) => c.id);
  const off = ro || !s.enabled;
  const sanctions = s.action !== "alert";
  const setCats = (ids) => set({ ...s, categories: NETBAN_CATEGORIES.map((c) => c.id).filter((id) => ids.includes(id)) });
  const toggleCat = (id) => setCats(cats.includes(id) ? cats.filter((x) => x !== id) : [...cats, id]);
  const presets = [
    ["Graves seulement", NETBAN_CATEGORIES.filter((c) => c.severity === 3).map((c) => c.id)],
    ["Graves et moyens", NETBAN_CATEGORIES.filter((c) => c.severity >= 2).map((c) => c.id)],
    ["Toutes", NETBAN_CATEGORIES.map((c) => c.id)],
  ];
  return (
    <div className="space-y-4">
      <Panel title="Bans réseau VantaPulse" icon={Globe}>
        <p className="text-xs text-text-muted">Quand un membre est banni d'un serveur du réseau, la raison du ban est triée (triche, arnaque…) avec sa gravité. Activé ici, le bot s'occupe des bannis du réseau qui arrivent sur ce serveur, ou qui y sont déjà.</p>
        <SwitchRow label="Appliquer les bans réseau sur ce serveur" help="Désactivé par défaut" checked={s.enabled} onChange={(v) => set({ ...s, enabled: v })} disabled={ro} testId="netban-enabled" />
        <Row label="Sanction" help={action.help}>
          <div className="flex flex-wrap border border-white/10 w-fit">
            {NETBAN_ACTIONS.map((a) => (
              <button key={a.value} type="button" disabled={off} onClick={() => set({ ...s, action: a.value })} data-testid={`netban-action-${a.value}`}
                className={`px-3 py-1.5 text-xs ${s.action === a.value ? (a.value === "quarantine" || a.value === "alert" ? "bg-blue-600 text-white" : "bg-red-600 text-white") : "text-text-muted hover:text-white"} disabled:cursor-not-allowed disabled:opacity-60`}>
                {a.label}
              </button>
            ))}
          </div>
        </Row>
        {s.action === "quarantine" && (
          <Row label="Seul salon visible" help="Le bot crée le rôle « 🚫 Banni VantaPulse » qui ne voit que ce salon">
            <ChannelSelect value={s.channel} onChange={(v) => set({ ...s, channel: v })} disabled={off} channels={channels} emptyLabel="Choisir un salon" testId="netban-channel" />
          </Row>
        )}
        {s.enabled && s.action === "quarantine" && !s.channel && (
          <p className="text-xs text-amber-200 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Choisis le salon auquel les bannis auront accès.</p>
        )}
      </Panel>

      {sanctions && (
        <Panel title="Raisons sanctionnées" icon={Tags}>
          <p className="text-xs text-text-muted">La sanction ne s'applique qu'aux bans de ces catégories. Les autres bannis, et ceux bannis <strong className="text-white/80">sans raison</strong>, sont laissés entrer : tu es seulement prévenu.</p>
          <div className="flex flex-wrap gap-1.5">
            {presets.map(([label, ids]) => {
              const on = ids.length === cats.length && ids.every((id) => cats.includes(id));
              return (
                <button key={label} type="button" disabled={off} onClick={() => setCats(ids)} data-testid={`netban-preset-${ids.length}`}
                  className={`px-2.5 py-1 text-[11px] border ${on ? "border-blue-500 bg-blue-600/20 text-white" : "border-white/10 text-text-muted hover:text-white"} disabled:opacity-60 disabled:cursor-not-allowed`}>{label}</button>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5" data-testid="netban-categories">
            {NETBAN_CATEGORIES.map((c) => {
              const on = cats.includes(c.id);
              const [sev, cls] = SEVERITY_BADGE[c.severity];
              return (
                <button key={c.id} type="button" disabled={off} onClick={() => toggleCat(c.id)} aria-pressed={on} data-testid={`netban-cat-${c.id}`}
                  className={`flex items-center gap-2.5 px-3 py-2 border text-left ${on ? "border-blue-500/60 bg-blue-600/10" : "border-white/10 bg-white/[0.02]"} disabled:opacity-60 disabled:cursor-not-allowed`}>
                  <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${on ? "bg-blue-600 border-blue-500" : "border-white/25"}`}>{on && <Check className="w-3 h-3" />}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm">{c.label}</span>
                    <span className="block text-[11px] text-white/40 truncate">{c.example}</span>
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 border ${cls}`}>{sev}</span>
                </button>
              );
            })}
          </div>
          {s.enabled && cats.length === 0 && <p className="text-xs text-amber-200 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Aucune catégorie : personne ne sera sanctionné, tu seras seulement prévenu.</p>}
          <p className="text-[11px] text-white/35">Astuce pour tes propres bans : écris un hashtag dans la raison du ban Discord, par exemple <code className="text-blue-300">#triche mod menu</code> ou <code className="text-blue-300">#arnaque</code>. Sans hashtag, les mots de la raison puis l'IA font le tri.</p>
        </Panel>
      )}

      <Panel title="Être prévenu" icon={Bell}>
        <Row label="Prévenir" help="Quand un banni du réseau arrive (ou est déjà là) : raison, catégorie, gravité et ce que le bot a fait">
          <div className="flex flex-wrap border border-white/10 w-fit">
            {NETBAN_ALERTS.map((a) => (
              <button key={a.value} type="button" disabled={off} onClick={() => set({ ...s, alert: a.value })} data-testid={`netban-alert-${a.value}`}
                className={`px-3 py-1.5 text-xs ${(s.alert || "owner") === a.value ? "bg-blue-600 text-white" : "text-text-muted hover:text-white"} disabled:cursor-not-allowed disabled:opacity-60`}>
                {a.label}
              </button>
            ))}
          </div>
        </Row>
        {s.alert === "channel" && (
          <Row label="Salon des alertes" help="Un salon réservé au staff, de préférence">
            <ChannelSelect value={s.alert_channel} onChange={(v) => set({ ...s, alert_channel: v })} disabled={off} channels={channels} emptyLabel="Choisir un salon" testId="netban-alert-channel" />
          </Row>
        )}
        {s.enabled && s.alert === "channel" && !s.alert_channel && (
          <p className="text-xs text-amber-200 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Choisis le salon où envoyer les alertes.</p>
        )}
      </Panel>

      {sanctions && (
        <Panel title="Message au membre" icon={MessageSquare}>
          {s.action === "quarantine" && <NetbanMessage s={s} set={set} ro={ro} field="message_quarantine" label="Message dans le salon" help="Envoyé dans le seul salon visible, en le mentionnant" />}
          {s.action === "kick" && <NetbanMessage s={s} set={set} ro={ro} field="message_kick" label="Message privé avant l'exclusion" />}
          {s.action === "ban" && <NetbanMessage s={s} set={set} ro={ro} field="message_ban" label="Message privé avant le ban" />}
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            {[["{user}", "mention"], ["{username}", "pseudo"], ["{server}", "serveur"], ["{reason}", "raison du ban réseau"]].map(([k, l]) => (
              <span key={k} className="px-1.5 py-0.5 bg-white/5 border border-white/10"><code className="text-blue-300">{k}</code> {l}</span>
            ))}
          </div>
        </Panel>
      )}
      <p className="text-[11px] text-white/35">Le rôle du bot doit être au-dessus des rôles des membres, avec les permissions Gérer les rôles, Gérer les salons, Expulser et Bannir. Chaque décision apparaît dans le journal du serveur (onglet Logs).</p>
    </div>
  );
}

function AutomodTab({ s, set, ro }) {
  const sp = s.antispam;
  const setSpam = (patch) => set({ ...s, antispam: { ...sp, ...patch } });
  const [word, setWord] = useState("");
  const addWords = () => {
    const words = word.split(/[,\n]/).map((w) => w.trim()).filter(Boolean);
    const next = [...s.badwords.words];
    words.forEach((w) => { if (!next.some((x) => x.toLowerCase() === w.toLowerCase())) next.push(w); });
    set({ ...s, badwords: { ...s.badwords, words: next.slice(0, 300) } });
    setWord("");
  };
  return (
    <div className="space-y-4">
      <Panel title="Anti-spam" icon={Shield}>
        <SwitchRow label="Anti-spam" help="Supprime le spam et exclut temporairement l'auteur" checked={sp.enabled} onChange={(v) => setSpam({ enabled: v })} disabled={ro} testId="antispam-enabled" />
        <Row label="Messages maximum"><NumberInput value={sp.messages} onChange={(v) => setSpam({ messages: v })} disabled={ro || !sp.enabled} min={2} max={30} suffix="messages" /></Row>
        <Row label="En"><NumberInput value={sp.seconds} onChange={(v) => setSpam({ seconds: v })} disabled={ro || !sp.enabled} min={1} max={60} suffix="secondes" /></Row>
        <Row label="Messages identiques" help="0 : pas de limite"><NumberInput value={sp.duplicates} onChange={(v) => setSpam({ duplicates: v })} disabled={ro || !sp.enabled} min={0} max={20} /></Row>
        <Row label="Mentions par message" help="0 : pas de limite"><NumberInput value={sp.mentions} onChange={(v) => setSpam({ mentions: v })} disabled={ro || !sp.enabled} min={0} max={50} /></Row>
        <Row label="Exclusion" help="0 : supprimer seulement"><NumberInput value={sp.timeoutMinutes} onChange={(v) => setSpam({ timeoutMinutes: v })} disabled={ro || !sp.enabled} min={0} max={1440} suffix="minutes" /></Row>
      </Panel>
      <Panel title="Mots bannis" icon={X}>
        <SwitchRow label="Mots bannis" help="Message supprimé ; exclu 5 min après 3 fois en 10 min" checked={s.badwords.enabled} onChange={(v) => set({ ...s, badwords: { ...s.badwords, enabled: v } })} disabled={ro} testId="badwords-enabled" />
        {!ro && (
          <div className="flex gap-2">
            <TextInput value={word} onChange={setWord} placeholder="mot, autre mot…" testId="badword-input" />
            <Btn onClick={addWords} disabled={!word.trim()} testId="badword-add"><Plus className="w-4 h-4" />Ajouter</Btn>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {s.badwords.words.length === 0 && <span className="text-xs text-text-muted">Aucun mot</span>}
          {s.badwords.words.map((w) => (
            <span key={w} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-500/10 border border-red-500/30">
              {w}
              {!ro && <button type="button" aria-label={`Retirer ${w}`} onClick={() => set({ ...s, badwords: { ...s.badwords, words: s.badwords.words.filter((x) => x !== w) } })}><X className="w-3 h-3" /></button>}
            </span>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function LogsTab({ s, set, ro, channels, meta, botId, guildId }) {
  return (
    <div className="space-y-4">
      <ServerJournal botId={botId} guildId={guildId} />
      <Panel title="Salons de logs sur Discord" icon={ScrollText}>
        <p className="text-xs text-text-muted">En plus du journal ci-dessus (toujours actif), le bot peut envoyer les logs dans des salons Discord. Choisis un salon pour chaque type, ou « Désactivé ».</p>
        {meta.log_types.map((t) => (
          <Row key={t.key} label={t.label}>
            <ChannelSelect value={s[t.key]} onChange={(v) => set({ ...s, [t.key]: v })} disabled={ro} channels={channels} emptyLabel="Désactivé" testId={`logs-${t.key}`} />
          </Row>
        ))}
      </Panel>
    </div>
  );
}

// ── Journal du serveur : tout ce que le bot voit, envoyé au site (toujours actif) ──

const LOG_GROUPS = [
  { id: "", label: "Tout" }, { id: "members", label: "Membres" }, { id: "moderation", label: "Modération" }, { id: "messages", label: "Messages" },
  { id: "voice", label: "Vocal" }, { id: "roles", label: "Rôles" }, { id: "server", label: "Serveur" }, { id: "tickets", label: "Tickets" },
];
const LOG_TYPES = {
  join: { label: "a rejoint le serveur", icon: DoorOpen, cls: "text-emerald-300 bg-emerald-500/10" },
  leave: { label: "a quitté le serveur", icon: DoorClosed, cls: "text-rose-300 bg-rose-500/10" },
  nick: { label: "a changé de pseudo", icon: Pencil, cls: "text-sky-300 bg-sky-500/10" },
  ban: { label: "a été banni", icon: Gavel, cls: "text-red-300 bg-red-500/15" },
  unban: { label: "a été débanni", icon: Gavel, cls: "text-emerald-300 bg-emerald-500/10" },
  netban: { label: "ban réseau", icon: Globe, cls: "text-red-300 bg-red-500/15" },
  kick: { label: "a été expulsé", icon: UserX, cls: "text-orange-300 bg-orange-500/10" },
  timeout: { label: "est exclu temporairement", icon: Clock, cls: "text-amber-300 bg-amber-500/10" },
  timeout_end: { label: "n'est plus exclu", icon: Clock, cls: "text-emerald-300 bg-emerald-500/10" },
  message_delete: { label: "message supprimé", icon: Trash2, cls: "text-rose-300 bg-rose-500/10" },
  message_edit: { label: "message modifié", icon: Pencil, cls: "text-sky-300 bg-sky-500/10" },
  voice_join: { label: "a rejoint un vocal", icon: Volume2, cls: "text-emerald-300 bg-emerald-500/10" },
  voice_leave: { label: "a quitté un vocal", icon: Volume2, cls: "text-rose-300 bg-rose-500/10" },
  voice_move: { label: "a changé de vocal", icon: Volume2, cls: "text-sky-300 bg-sky-500/10" },
  role_add: { label: "rôle ajouté", icon: Tags, cls: "text-emerald-300 bg-emerald-500/10" },
  role_remove: { label: "rôle retiré", icon: Tags, cls: "text-rose-300 bg-rose-500/10" },
  role_create: { label: "Rôle créé", icon: Tags, cls: "text-violet-300 bg-violet-500/10" },
  role_delete: { label: "Rôle supprimé", icon: Tags, cls: "text-violet-300 bg-violet-500/10" },
  channel_create: { label: "Salon créé", icon: Hash, cls: "text-violet-300 bg-violet-500/10" },
  channel_delete: { label: "Salon supprimé", icon: Hash, cls: "text-violet-300 bg-violet-500/10" },
  ticket_open: { label: "a ouvert un ticket", icon: Ticket, cls: "text-blue-300 bg-blue-500/10" },
  ticket_claim: { label: "ticket pris en charge", icon: Ticket, cls: "text-amber-300 bg-amber-500/10" },
  ticket_close: { label: "ticket fermé", icon: Ticket, cls: "text-text-muted bg-white/5" },
};

// <t:1760000000:R> (heures Discord) → texte lisible.
const discordTimes = (text) => (text || "").replace(/<t:(\d+)(?::([a-zA-Z]))?>/g, (_, ts, f) => {
  const d = new Date(Number(ts) * 1000);
  return f === "R" ? since(d.toISOString()) : d.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
});

function JournalRow({ e }) {
  const t = LOG_TYPES[e.type] || { label: e.type, icon: Activity, cls: "text-text-muted bg-white/5" };
  const Icon = t.icon;
  const subject = e.user_name || (e.type.startsWith("channel_") ? `#${e.channel_name}` : "");
  const quote = ["message_delete", "message_edit"].includes(e.type);
  return (
    <div className="flex gap-3 px-4 py-3 hover:bg-white/[0.02]" data-testid="journal-row">
      <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${t.cls}`}><Icon className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
          {e.user_avatar && <img src={e.user_avatar} alt="" className="w-5 h-5 rounded-full" />}
          {subject && <span className="font-medium text-white">{subject}</span>}
          {e.user_bot && <span className="text-[10px] px-1 rounded bg-indigo-500/80 text-white">BOT</span>}
          <span className="text-text-muted">{t.label}</span>
          {e.channel_name && !e.type.startsWith("channel_") && <span className="text-text-muted">dans <span className="text-white/80">#{e.channel_name}</span></span>}
          {e.type === "voice_move" && e.extra && <span className="text-text-muted">(depuis {e.extra})</span>}
          {e.type === "kick" && e.extra && <span className="text-text-muted">par <span className="text-white/80">{e.extra}</span></span>}
          {e.type === "ticket_claim" && e.extra && <span className="text-text-muted">par <span className="text-white/80">{e.extra}</span></span>}
          <span className="ml-auto text-[11px] text-text-muted shrink-0" title={new Date(e.at).toLocaleString("fr-FR")}>{since(e.at)}</span>
        </div>
        {e.text && (quote
          ? <p className="mt-1 text-sm text-white/80 border-l-2 border-white/15 pl-2 whitespace-pre-wrap break-words">{e.text}</p>
          : <p className="mt-0.5 text-xs text-text-muted break-words">{e.type === "nick" ? `${e.extra || "?"} → ${e.text}` : discordTimes(e.text)}</p>)}
        {e.type === "message_edit" && e.extra && <p className="mt-1 text-xs text-text-muted line-through break-words">{e.extra}</p>}
        {e.type === "message_delete" && e.extra && <p className="mt-0.5 text-xs text-text-muted">{e.extra}</p>}
        {["ban", "ticket_close"].includes(e.type) && e.extra && <p className="mt-0.5 text-xs text-text-muted">{e.extra}</p>}
      </div>
    </div>
  );
}

function ServerJournal({ botId, guildId }) {
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);     // 100 derniers événements, relus toutes les 10 s
  const [older, setOlder] = useState([]);     // pages plus anciennes chargées à la demande
  const [olderMore, setOlderMore] = useState(null);
  const [more, setMore] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const params = useMemo(() => new URLSearchParams({ ...(group ? { group } : {}), ...(query ? { q: query } : {}) }).toString(), [group, query]);
  const url = `/bots/${botId}/guilds/${guildId}/logs?${params}`;
  const load = useCallback(() => dash.get(url).then(setData).catch(() => setData((d) => d || { logs: [] })), [url]);
  useEffect(() => {
    setData(null); setOlder([]); setOlderMore(null);
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);
  const rows = useMemo(() => {
    const recent = data?.logs || [];
    const seen = new Set(recent.map((x) => x.id));
    return [...recent, ...older.filter((x) => !seen.has(x.id))];
  }, [data, older]);
  const hasMore = older.length ? olderMore : data?.has_more;
  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    setMore(true);
    try {
      const r = await dash.get(`${url}${params ? "&" : ""}before=${encodeURIComponent(last.at)}`);
      setOlder((o) => [...o, ...r.logs]);
      setOlderMore(r.has_more);
    } catch { /* réessaie plus tard */ }
    setMore(false);
  };
  return (
    <Panel title="Journal du serveur" icon={Activity} className="overflow-hidden" actions={
      <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Toujours actif</span>
    }>
      <p className="text-xs text-text-muted -mt-1">Tout ce qui se passe sur le serveur, envoyé par le bot (arrivées, départs, bans, expulsions, messages supprimés ou modifiés, vocal, rôles, pseudos, salons, tickets). Les 3000 derniers événements sont gardés.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1" role="tablist">
          {LOG_GROUPS.map((g) => (
            <button key={g.id || "all"} type="button" onClick={() => setGroup(g.id)} data-testid={`journal-group-${g.id || "all"}`}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${group === g.id ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-text-muted hover:text-white hover:border-white/25"}`}>{g.label}</button>
          ))}
        </div>
        <label className="relative sm:w-56">
          <Search className="w-4 h-4 text-text-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Membre, texte, salon…" className={`${inputCls} pl-8`} data-testid="journal-search" />
        </label>
      </div>
      <div className="-mx-4 -mb-4 border-t border-white/5 divide-y divide-white/5 max-h-[560px] overflow-y-auto" data-testid="journal">
        {!data && <div className="p-6 text-center text-sm text-text-muted"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>}
        {data && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-text-muted">
            {query || group ? "Rien ne correspond." : data.online ? "Rien pour l'instant : les prochains événements apparaîtront ici." : "Rien pour l'instant. Le bot doit être à jour et connecté au site pour remplir le journal."}
          </div>
        )}
        {rows.map((e) => <JournalRow key={e.id} e={e} />)}
        {hasMore && <div className="p-3 text-center"><Btn onClick={loadMore} disabled={more}>{more ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Plus ancien</Btn></div>}
      </div>
    </Panel>
  );
}

function LevelingTab({ s, set, ro, channels, roles }) {
  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));
  const [lvl, setLvl] = useState(5);
  const [role, setRole] = useState("");
  const addReward = () => {
    if (!role || s.rewards.some((r) => r.level === lvl || r.roleId === role)) { toast.error("Ce niveau ou ce rôle est déjà utilisé"); return; }
    set({ ...s, rewards: [...s.rewards, { level: lvl, roleId: role }].sort((a, b) => a.level - b.level) });
    setRole("");
  };
  return (
    <div className="space-y-4">
      <Panel title="Niveaux" icon={Sparkles}>
        <SwitchRow label="Système de niveaux" help="Les membres gagnent de l'XP en écrivant et en vocal (100 XP = 1 niveau)" checked={s.enabled} onChange={(v) => set({ ...s, enabled: v })} disabled={ro} testId="leveling-enabled" />
        <Row label="XP par message"><NumberInput value={s.messageRate} onChange={(v) => set({ ...s, messageRate: v })} disabled={ro} min={1} max={1000} suffix="XP" /></Row>
        <Row label="XP par minute en vocal"><NumberInput value={s.voiceRate} onChange={(v) => set({ ...s, voiceRate: v })} disabled={ro} min={0} max={1000} suffix="XP" /></Row>
        <Row label="Délai entre deux gains"><NumberInput value={s.cooldown} onChange={(v) => set({ ...s, cooldown: v })} disabled={ro} min={0} max={3600} suffix="secondes" /></Row>
        <SwitchRow label="Annoncer les passages de niveau" checked={s.levelUpMessage} onChange={(v) => set({ ...s, levelUpMessage: v })} disabled={ro} />
        <Row label="Salon des annonces" help="Vide : le salon où le membre écrit">
          <ChannelSelect value={s.levelUpChannel} onChange={(v) => set({ ...s, levelUpChannel: v })} disabled={ro || !s.levelUpMessage} channels={channels} emptyLabel="Salon du message" />
        </Row>
      </Panel>
      <Panel title="Rôles de niveau" icon={Crown}>
        <SwitchRow label="Cumuler les rôles" help="Garder les rôles des niveaux précédents" checked={s.cumul} onChange={(v) => set({ ...s, cumul: v })} disabled={ro} />
        <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden">
          {s.rewards.length === 0 && <div className="p-3 text-xs text-text-muted">Aucun rôle de niveau</div>}
          {s.rewards.map((r) => (
            <div key={r.roleId} className="flex items-center justify-between gap-3 p-2.5">
              <span className="text-sm">Niveau <b>{r.level}</b></span>
              <RoleChip role={byId[r.roleId]} id={r.roleId} disabled={ro} onRemove={() => set({ ...s, rewards: s.rewards.filter((x) => x !== r) })} />
            </div>
          ))}
        </div>
        {!ro && s.rewards.length < 25 && (
          <div className="flex flex-wrap gap-2 items-center">
            <NumberInput value={lvl} onChange={setLvl} min={1} max={1000} />
            {roles.length ? (
              <div className="flex-1 min-w-[180px]"><Picker value={role} onChange={setRole} placeholder="Choisir un rôle…" searchPlaceholder="Rechercher un rôle"
                options={assignable(roles).map((r) => ({ value: r.id, label: r.name, color: roleColor(r) }))} /></div>
            ) : <div className="flex-1"><TextInput value={role} onChange={setRole} placeholder="ID du rôle" /></div>}
            <Btn onClick={addReward} disabled={!role}><Plus className="w-4 h-4" />Ajouter</Btn>
          </div>
        )}
      </Panel>
    </div>
  );
}

function NotificationsTab({ s, set, ro, channels, meta }) {
  const [form, setForm] = useState({ platform: "youtube", kind: "all", link: "", channelId: "", message: "" });
  const kinds = meta.notif_platforms[form.platform] || [];
  const add = () => {
    if (!form.link.trim() || !form.channelId) { toast.error("Lien et salon obligatoires"); return; }
    set({ ...s, alerts: [...s.alerts, { ...form, link: form.link.trim() }] });
    setForm({ ...form, link: "", message: "" });
  };
  const update = (i, patch) => set({ ...s, alerts: s.alerts.map((a, j) => (j === i ? { ...a, ...patch } : a)) });
  return (
    <div className="space-y-4">
      <Panel title={`Alertes (${s.alerts.length}/${meta.max_alerts})`} icon={Bell}>
        <p className="text-xs text-text-muted">Le bot vérifie les chaînes toutes les 3 minutes et annonce seulement ce qui est nouveau. Une nouvelle alerte est vérifiée par le bot : si la chaîne n'existe pas, elle est refusée.</p>
        <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden">
          {s.alerts.length === 0 && <div className="p-3 text-xs text-text-muted">Aucune alerte</div>}
          {s.alerts.map((a, i) => (
            <div key={a.id || `new-${i}`} className="p-3 space-y-2" data-testid="notif-alert">
              <div className="flex items-center gap-2">
                <span className="text-xs px-1.5 py-0.5 bg-white/5 border border-white/10">{PLATFORM_LABELS[a.platform]}</span>
                <span className="text-xs text-text-muted">{KIND_LABELS[a.kind]}</span>
                <span className="text-sm truncate flex-1">{a.name || a.link}</span>
                {!a.id && <span className="text-[11px] text-amber-300" data-testid="notif-new">nouvelle</span>}
                {!ro && <button type="button" aria-label="Supprimer l'alerte" className="text-text-muted hover:text-red-300" onClick={() => set({ ...s, alerts: s.alerts.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <ChannelSelect value={a.channelId} onChange={(v) => update(i, { channelId: v })} disabled={ro} channels={channels} allowEmpty={false} emptyLabel="Choisir un salon" />
                <TextInput value={a.message} onChange={(v) => update(i, { message: v })} disabled={ro} placeholder="Message (vide : message par défaut)" maxLength={1500} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
      {!ro && s.alerts.length < meta.max_alerts && (
        <Panel title="Nouvelle alerte" icon={Plus}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Select value={form.platform} testId="notif-platform" onChange={(v) => setForm({ ...form, platform: v, kind: meta.notif_platforms[v][0] })}
              options={Object.keys(meta.notif_platforms).map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))} />
            <Select value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} options={kinds.map((k) => ({ value: k, label: KIND_LABELS[k] }))} />
          </div>
          <TextInput value={form.link} onChange={(v) => setForm({ ...form, link: v })} placeholder={LINK_HELP[form.platform]} maxLength={300} testId="notif-link" />
          <ChannelSelect value={form.channelId} onChange={(v) => setForm({ ...form, channelId: v })} channels={channels} emptyLabel="Choisir le salon des annonces" testId="notif-channel" />
          <TextInput value={form.message} onChange={(v) => setForm({ ...form, message: v })} placeholder="Message (vide : par défaut). {name}, {title}, {url}, {platform}" maxLength={1500} />
          <Btn variant="primary" onClick={add} testId="notif-add"><Plus className="w-4 h-4" />Ajouter l'alerte</Btn>
        </Panel>
      )}
    </div>
  );
}

function Person({ p, badge, me, onRemove }) {
  return (
    <div className="flex items-center gap-3 p-2.5" data-testid="team-person">
      <img src={p.avatar_url} alt="" className="w-8 h-8 rounded-full bg-white/10" />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{p.username || "Utilisateur Discord"} {me && <span className="text-[11px] text-blue-300">(toi)</span>}</div>
        <div className="text-[11px] text-text-muted font-mono">{p.id}</div>
      </div>
      {badge}
      {onRemove && <Btn variant="danger" onClick={onRemove} title="Retirer"><Trash2 className="w-3.5 h-3.5" /></Btn>}
    </div>
  );
}

// Onglet « Commandes » : toutes les commandes du bot, écrites avec le préfixe réglé,
// comme le +help sur Discord (recherche, catégories, utilisation et alias).
function CommandsTab({ botId, go }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(null);
  useEffect(() => {
    dash.get(`/bots/${botId}/commands`).then(setData).catch((err) => toast.error(errorText(err, "Commandes introuvables")));
  }, [botId]);
  if (!data) return <p className="text-sm text-text-muted">Chargement…</p>;
  const p = data.prefix || "+";
  let needle = q.trim().toLowerCase();
  // « !ban » ou « +ban » → « ban » : le préfixe tapé devant le nom est ignoré.
  if (needle.startsWith(p.toLowerCase())) needle = needle.slice(p.length).trim();
  else needle = needle.replace(/^[^\p{L}\p{N}]+/u, "");
  const match = (c) => !needle || c.name.includes(needle) || c.aliases.some((a) => a.includes(needle)) || c.description.toLowerCase().includes(needle);
  const shown = data.categories
    .filter((c) => cat === "all" || c.id === cat)
    .map((c) => ({ ...c, commands: c.commands.filter(match) }))
    .filter((c) => c.commands.length);
  const count = shown.reduce((n, c) => n + c.commands.length, 0);
  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copié"), () => {});
  return (
    <div className="space-y-4" data-testid="commands-tab">
      <Panel title={`Commandes (${data.total})`} icon={BookOpen}
        actions={<Btn onClick={() => go("bot")} title="Changer le préfixe dans l'onglet Bot" testId="commands-prefix">Préfixe : <code className="font-mono text-blue-200">{p}</code></Btn>}>
        <p className="text-sm text-white/55">
          La même aide que <code className="font-mono text-white/80">{p}help</code> sur Discord, avec ton préfixe. Sur ton serveur,
          {" "}<code className="font-mono text-white/80">{p}help commande</code> affiche l'aide d'une seule commande.
        </p>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher une commande (nom, alias, description)…"
            className={`${inputCls} pl-9`} data-testid="commands-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[{ id: "all", label: "Toutes", n: data.total }, ...data.categories.map((c) => ({ id: c.id, label: c.label, n: c.commands.length }))].map((c) => (
            <button key={c.id} type="button" onClick={() => setCat(c.id)} data-testid={`commands-cat-${c.id}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${cat === c.id ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-white/60 hover:bg-white/5"}`}>
              {c.label} <span className="opacity-60">{c.n}</span>
            </button>
          ))}
        </div>
      </Panel>
      {count === 0 && <p className="text-sm text-white/40">Aucune commande ne correspond.</p>}
      {shown.map((c) => (
        <Panel key={c.id} title={`${c.label} (${c.commands.length})`}>
          <div className="divide-y divide-white/[0.06] -my-2">
            {c.commands.map((cmd) => {
              const isOpen = open === cmd.name;
              const usage = cmd.usage.length ? cmd.usage : [cmd.name];
              return (
                <div key={cmd.name} className="py-2" data-testid="command-row">
                  <button type="button" onClick={() => setOpen(isOpen ? null : cmd.name)} className="w-full flex items-start gap-3 text-left">
                    <code className="font-mono text-sm text-blue-200 shrink-0 min-w-[7rem]">{p}{cmd.name}</code>
                    <span className="text-sm text-white/65 flex-1 min-w-0">{cmd.description || "—"}</span>
                    <ChevronDown className={`w-4 h-4 mt-0.5 shrink-0 text-white/35 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="mt-2 ml-1 pl-3 border-l-2 border-blue-400/30 space-y-2 text-sm">
                      <div>
                        <p className="text-xs text-white/40 mb-1">Utilisation</p>
                        {usage.map((u) => (
                          <div key={u} className="flex items-center gap-2">
                            <code className="font-mono text-white/85 bg-white/5 rounded px-1.5 py-0.5 break-all">{p}{u}</code>
                            <button type="button" onClick={() => copy(`${p}${u}`)} className="text-white/35 hover:text-white" title="Copier"><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                      {cmd.aliases.length > 0 && (
                        <p className="text-white/55"><span className="text-xs text-white/40">Aussi : </span>{cmd.aliases.map((a) => <code key={a} className="font-mono text-white/75 mr-2">{p}{a}</code>)}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function TeamTab({ botId }) {
  const [team, setTeam] = useState(null);
  const [input, setInput] = useState({ owners: "", buyers: "" });
  const load = useCallback(() => dash.get(`/bots/${botId}/team`).then(setTeam).catch((err) => toast.error(errorText(err, "Équipe introuvable"))), [botId]);
  useEffect(() => { load(); }, [load]);
  if (!team) return <p className="text-sm text-text-muted">Chargement…</p>;
  const perms = team.permissions;
  const add = async (kind) => {
    try { await dash.post(`/bots/${botId}/team/${kind}`, { discord_id: input[kind].trim() }); setInput({ ...input, [kind]: "" }); toast.success("Ajouté : le bot le prend en compte d'ici quelques minutes"); load(); } catch (err) { toast.error(errorText(err, "Ajout impossible")); }
  };
  const remove = async (kind, id) => {
    if (!window.confirm("Retirer cette personne ?")) return;
    try { await dash.del(`/bots/${botId}/team/${kind}/${id}`); toast.success("Retiré"); load(); } catch (err) { toast.error(errorText(err, "Erreur")); }
  };
  const list = (kind, title, icon, canManage, help) => (
    <Panel title={`${title} (${team[kind].length})`} icon={icon}>
      <p className="text-xs text-text-muted">{help}</p>
      <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden">
        {team[kind].length === 0 && <div className="p-3 text-xs text-text-muted">Personne</div>}
        {team[kind].map((p) => <Person key={p.id} p={p} me={p.id === team.me} onRemove={canManage ? () => remove(kind, p.id) : null} />)}
      </div>
      {canManage && (
        <div className="flex gap-2">
          <TextInput value={input[kind]} onChange={(v) => setInput({ ...input, [kind]: v })} placeholder="ID Discord (clic droit sur le profil > Copier l'identifiant)" testId={`team-input-${kind}`} />
          <Btn variant="primary" onClick={() => add(kind)} disabled={!input[kind].trim()} testId={`team-add-${kind}`}><Plus className="w-4 h-4" />Ajouter</Btn>
        </div>
      )}
    </Panel>
  );
  return (
    <div className="space-y-4">
      {team.acheteur && (
        <Panel title="Acheteur" icon={Crown}>
          <Person p={team.acheteur} me={team.acheteur.id === team.me} badge={<span className="text-[11px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-200">Acheteur</span>} />
        </Panel>
      )}
      {list("buyers", "Buyers", Sparkles, perms.manage_buyers, "Accès complet au bot, comme l'acheteur (sauf ajouter des buyers).")}
      {list("owners", "Owners", Shield, perms.manage_owners, "Commandes de modération et sécurité ; pas les réglages réservés aux buyers.")}
    </div>
  );
}

// ── Réactions automatiques ──

function AutoreactTab({ s, set, ro, channels }) {
  const [channel, setChannel] = useState("");
  const [emoji, setEmoji] = useState("");
  const byId = Object.fromEntries(channels.map((c) => [c.id, c]));
  const add = () => {
    if (!channel || !emoji.trim()) { toast.error("Choisis un salon et un emoji"); return; }
    if (s.rules.some((r) => r.channel === channel && r.emoji === emoji.trim())) return;
    set({ ...s, rules: [...s.rules, { channel, emoji: emoji.trim() }] });
    setEmoji("");
  };
  return (
    <Panel title={`Réactions automatiques (${s.rules.length}/50)`} icon={Smile}>
      <p className="text-xs text-text-muted">Le bot ajoute ces réactions à chaque message envoyé dans le salon (suggestions, annonces, photos…). Emoji du serveur : <code className="text-blue-300">&lt;:nom:id&gt;</code>.</p>
      <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden">
        {s.rules.length === 0 && <div className="p-3 text-xs text-text-muted">Aucune réaction automatique</div>}
        {s.rules.map((r, i) => (
          <div key={`${r.channel}-${r.emoji}`} className="flex items-center gap-3 p-2.5" data-testid="autoreact-rule">
            <span className="text-sm flex-1"># {byId[r.channel]?.name || r.channel}</span>
            <span className="text-lg">{r.emoji.startsWith("<") ? "🙂" : r.emoji}</span>
            {r.emoji.startsWith("<") && <code className="text-[11px] text-text-muted">{r.emoji}</code>}
            {!ro && <button type="button" aria-label="Supprimer" onClick={() => set({ ...s, rules: s.rules.filter((_, j) => j !== i) })} className="text-text-muted hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      {!ro && s.rules.length < 50 && (
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[200px]"><ChannelSelect value={channel} onChange={setChannel} channels={channels} emptyLabel="Choisir un salon" testId="autoreact-channel" /></div>
          <div className="w-40"><TextInput value={emoji} onChange={setEmoji} placeholder="👍" maxLength={64} testId="autoreact-emoji" /></div>
          <Btn variant="primary" onClick={add} testId="autoreact-add"><Plus className="w-4 h-4" />Ajouter</Btn>
        </div>
      )}
    </Panel>
  );
}

// ── Tickets ──

function CategorySelect({ value, onChange, disabled, channels, emptyLabel = "Aucune", testId }) {
  const options = useMemo(() => channels.filter((c) => c.type === 4).map((c) => ({ value: c.id, label: c.name, icon: Folder })), [channels]);
  if (!channels.length) return <TextInput value={value} onChange={onChange} disabled={disabled} placeholder="ID de la catégorie" testId={testId} />;
  return <Picker value={value} options={options} onChange={onChange} disabled={disabled} testId={testId} empty={emptyLabel} placeholder={emptyLabel} searchPlaceholder="Rechercher une catégorie" />;
}

function ActionHistory({ actions, type }) {
  const list = actions.filter((a) => !type || a.type === type).slice(0, 6);
  if (!list.length) return null;
  const badge = {
    pending: ["En attente du bot", "text-amber-200 border-amber-500/30 bg-amber-500/10"],
    sent: ["En cours…", "text-amber-200 border-amber-500/30 bg-amber-500/10"],
    done: ["Fait", "text-emerald-200 border-emerald-500/30 bg-emerald-500/10"],
    error: ["Échec", "text-red-200 border-red-500/30 bg-red-500/10"],
  };
  return (
    <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden" data-testid="action-history">
      {list.map((a) => {
        const [label, cls] = badge[a.status] || badge.pending;
        return (
          <div key={a.id} className="p-2.5 text-sm flex flex-wrap items-center gap-2" data-testid={`action-${a.status}`}>
            <span className={`text-[11px] px-1.5 py-0.5 border ${cls}`}>{label}</span>
            <span className="flex-1 min-w-[160px] truncate">{a.summary}</span>
            <span className="text-[11px] text-text-muted">{since(a.created_at)}</span>
            {a.status === "error" && a.error && <div className="w-full text-xs text-red-300">{a.error}</div>}
            {a.status === "done" && a.result?.message_id && <div className="w-full text-[11px] text-text-muted font-mono">Message {a.result.message_id}</div>}
            {a.status === "done" && a.result?.detail && <div className="w-full text-xs text-white/60" data-testid="action-detail">{a.result.detail}</div>}
          </div>
        );
      })}
    </div>
  );
}

function useActions(botId, guildId) {
  const [actions, setActions] = useState([]);
  const load = useCallback(() => dash.get(`/bots/${botId}/guilds/${guildId}/actions`).then((r) => setActions(r.actions)).catch(() => {}), [botId, guildId]);
  useEffect(() => { load(); }, [load]);
  const busy = actions.some((a) => a.status === "pending" || a.status === "sent");
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [busy, load]);
  const run = async (type, payload, okText) => {
    try {
      const r = await dash.post(`/bots/${botId}/guilds/${guildId}/actions`, { type, payload });
      setActions((list) => [r.action, ...list]);
      toast.success(okText);
      return true;
    } catch (err) { toast.error(errorText(err, "Action impossible")); return false; }
  };
  const add = (action) => setActions((list) => [action, ...list]);
  return { actions, run, add };
}

function TicketOption({ o, i, count, update, remove, move, ro, channels, roles }) {
  // Un type qui vient d'être ajouté (nom par défaut) s'ouvre directement.
  const [open, setOpen] = useState(!o.label || o.label === "Support" || /^Type \d+$/.test(o.label));
  const set = (patch) => update(i, { ...o, ...patch });
  return (
    <div className="border border-white/10" data-testid="ticket-option">
      <div className="flex items-center gap-2 p-2.5 bg-white/[0.03]">
        <button type="button" className="flex-1 text-left text-sm flex items-center gap-2" onClick={() => setOpen(!open)}>
          <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
          <span>{o.emoji || "🎫"}</span><b>{o.label || "Sans nom"}</b>
          {o.description && <span className="text-xs text-text-muted truncate">— {o.description}</span>}
        </button>
        {!ro && (
          <>
            <button type="button" aria-label="Monter" disabled={i === 0} onClick={() => move(i, -1)} className="text-text-muted hover:text-white disabled:opacity-30 px-1">↑</button>
            <button type="button" aria-label="Descendre" disabled={i === count - 1} onClick={() => move(i, 1)} className="text-text-muted hover:text-white disabled:opacity-30 px-1">↓</button>
            <button type="button" aria-label="Supprimer ce type" onClick={() => remove(i)} className="text-text-muted hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
      {open && (
        <div className="p-3 space-y-3">
          <div className="grid sm:grid-cols-[1fr_120px] gap-2">
            <TextInput value={o.label} onChange={(v) => set({ label: v })} disabled={ro} placeholder="Nom du bouton (ex. Support)" maxLength={80} testId={`ticket-opt-label-${i}`} />
            <TextInput value={o.emoji} onChange={(v) => set({ emoji: v })} disabled={ro} placeholder="Emoji" maxLength={64} />
          </div>
          <TextInput value={o.description} onChange={(v) => set({ description: v })} disabled={ro} placeholder="Description (menu déroulant)" maxLength={100} />
          <Row label="Catégorie des tickets" help="Les salons de ticket sont créés dedans"><CategorySelect value={o.categoryId} onChange={(v) => set({ categoryId: v })} disabled={ro} channels={channels} emptyLabel="Aucune (en haut du serveur)" /></Row>
          <Row label="Nom du salon" help="{MemberName}, {TicketNumber}"><TextInput value={o.channelName} onChange={(v) => set({ channelName: v })} disabled={ro} maxLength={90} /></Row>
          <Row label="Message d'ouverture" help="Vide : message par défaut"><TextArea value={o.openMessage} onChange={(v) => set({ openMessage: v })} disabled={ro} maxLength={2000} placeholder="Bienvenue {MemberName}, explique ta demande." /></Row>
          <Row label="Staff qui voit ces tickets"><RolePicker value={o.accessRoles} onChange={(v) => set({ accessRoles: v })} disabled={ro} roles={roles} max={25} /></Row>
          <Row label="Rôles mentionnés à l'ouverture"><RolePicker value={o.mentionRoles} onChange={(v) => set({ mentionRoles: v })} disabled={ro} roles={roles} max={25} /></Row>
          <Row label="Rôles interdits pour ce type"><RolePicker value={o.forbiddenRoles} onChange={(v) => set({ forbiddenRoles: v })} disabled={ro} roles={roles} max={25} /></Row>
          <Row label="Salon de logs de ce type" help="Vide : celui des tickets"><ChannelSelect value={o.logChannelId} onChange={(v) => set({ logChannelId: v })} disabled={ro} channels={channels} emptyLabel="Celui des tickets" /></Row>
          <SwitchRow label="Membres sans rôle autorisés" checked={o.allowEveryone} onChange={(v) => set({ allowEveryone: v })} disabled={ro} />
          <SwitchRow label="Supprimer le salon à la fermeture" checked={o.deleteOnClose} onChange={(v) => set({ deleteOnClose: v })} disabled={ro} />
          <SwitchRow label="Transcript à la fermeture" checked={o.transcript} onChange={(v) => set({ transcript: v })} disabled={ro} />
        </div>
      )}
    </div>
  );
}

function TicketsTab({ s, set, ro, channels, roles, meta, botId, guildId, dirty, canAct }) {
  const { actions, run } = useActions(botId, guildId);
  const update = (i, o) => set({ ...s, options: s.options.map((x, j) => (j === i ? o : x)) });
  const remove = (i) => set({ ...s, options: s.options.filter((_, j) => j !== i) });
  const move = (i, d) => {
    const list = [...s.options];
    [list[i], list[i + d]] = [list[i + d], list[i]];
    set({ ...s, options: list });
  };
  const preview = {
    title: s.panelTitle, description: s.panelText || "Cliquez ci-dessous pour ouvrir un ticket.", color: s.panelColor, image: s.panelImage,
  };
  return (
    <div className="space-y-4">
      <Panel title="Panel des tickets" icon={Ticket}>
        <Row label="Salon du panel" help="Où le bot publie le message pour ouvrir un ticket">
          <ChannelSelect value={s.channelId} onChange={(v) => set({ ...s, channelId: v })} disabled={ro} channels={channels} emptyLabel="Choisir un salon" testId="tickets-channel" />
        </Row>
        <Row label="Mode">
          <Select value={s.mode} disabled={ro} onChange={(v) => set({ ...s, mode: v })}
            options={[{ value: "channel", label: "📁 Un salon par ticket" }, { value: "dm", label: "💬 En message privé avec le bot" }]} />
        </Row>
        {s.mode === "channel" && (
          <Row label="Présentation"><Select value={s.buttonType} disabled={ro} onChange={(v) => set({ ...s, buttonType: v })}
            options={[{ value: "buttons", label: "🔘 Un bouton par type" }, { value: "select", label: "📋 Menu déroulant" }]} /></Row>
        )}
        {s.mode === "dm" && (
          <Row label="Catégorie des tickets en MP"><CategorySelect value={s.dmCategoryId} onChange={(v) => set({ ...s, dmCategoryId: v })} disabled={ro} channels={channels} /></Row>
        )}
        <Row label="Titre de l'embed" help="Vide : pas de titre"><TextInput value={s.panelTitle} onChange={(v) => set({ ...s, panelTitle: v })} disabled={ro} maxLength={256} /></Row>
        <Row label="Texte de l'embed"><TextArea value={s.panelText} onChange={(v) => set({ ...s, panelText: v })} disabled={ro} maxLength={4000} rows={4} /></Row>
        <Row label="Couleur"><ColorInput value={s.panelColor} onChange={(v) => set({ ...s, panelColor: v })} disabled={ro} /></Row>
        <Row label="Image / bannière" help="Facultatif : envoie une image, colle un lien, ou laisse vide"><ImageField value={s.panelImage} onChange={(v) => set({ ...s, panelImage: v })} disabled={ro} testId="tickets-image" /></Row>
        <div>
          <div className="text-xs text-text-muted mb-1.5">Aperçu</div>
          <DiscordPreview message={{ content: "", embeds: [preview], buttons: [] }}
            extra={s.mode === "dm" ? [{ label: "📩 Ouvrir un ticket", primary: true }] : s.buttonType === "select" ? "select" : s.options.map((o) => ({ label: `${o.emoji ? `${o.emoji} ` : ""}${o.label}`, primary: true }))} />
        </div>
      </Panel>

      {s.mode === "channel" && (
        <Panel title={`Types de ticket (${s.options.length}/25)`} icon={Ticket} actions={!ro && s.options.length < 25 && (
          <Btn onClick={() => set({ ...s, options: [...s.options, { ...meta.ticket_option_default, label: s.options.length ? `Type ${s.options.length + 1}` : "Support" }] })} testId="ticket-add-option"><Plus className="w-4 h-4" />Ajouter</Btn>
        )}>
          {s.options.length === 0 && <p className="text-sm text-text-muted">Ajoute au moins un type de ticket (Support, Recrutement, Partenariat…).</p>}
          {s.options.map((o, i) => <TicketOption key={i} o={o} i={i} count={s.options.length} update={update} remove={remove} move={move} ro={ro} channels={channels} roles={roles} />)}
        </Panel>
      )}

      <Panel title="Fonctionnement" icon={Settings2}>
        <Row label="Salon de logs" help="Ouvertures, fermetures, transcripts"><ChannelSelect value={s.logChannelId} onChange={(v) => set({ ...s, logChannelId: v })} disabled={ro} channels={channels} emptyLabel="Aucun" /></Row>
        <Row label="Tickets ouverts par personne"><NumberInput value={s.maxPerUser} onChange={(v) => set({ ...s, maxPerUser: v })} disabled={ro} min={1} max={10} /></Row>
        <SwitchRow label="Prise en charge (claim)" help="Un membre du staff prend le ticket" checked={s.claim} onChange={(v) => set({ ...s, claim: v })} disabled={ro} />
        <SwitchRow label="Prise en charge automatique" help="Le premier staff qui répond prend le ticket" checked={s.autoclaim} onChange={(v) => set({ ...s, autoclaim: v })} disabled={ro} />
        <SwitchRow label="Bouton « Prendre en charge »" checked={s.claimButton} onChange={(v) => set({ ...s, claimButton: v })} disabled={ro} />
        <SwitchRow label="Bouton « Fermer »" checked={s.closeButton} onChange={(v) => set({ ...s, closeButton: v })} disabled={ro} />
        <SwitchRow label="Bouton « Renommer »" help="Le staff du ticket change le nom du salon" checked={s.renameButton} onChange={(v) => set({ ...s, renameButton: v })} disabled={ro} />
        <SwitchRow label="Bouton « Ajouter un membre »" help="Staff du ticket ou son créateur" checked={s.addButton} onChange={(v) => set({ ...s, addButton: v })} disabled={ro} />
        <SwitchRow label="Bouton « Retirer un membre »" help="Staff du ticket" checked={s.removeButton} onChange={(v) => set({ ...s, removeButton: v })} disabled={ro} />
        <SwitchRow label="Transcript en MP au membre" checked={s.transcript} onChange={(v) => set({ ...s, transcript: v })} disabled={ro} />
        <SwitchRow label="Supprimer le salon à la fermeture" checked={s.autoDelete} onChange={(v) => set({ ...s, autoDelete: v })} disabled={ro} />
        <SwitchRow label="Fermer si le membre quitte le serveur" checked={s.closeOnLeave} onChange={(v) => set({ ...s, closeOnLeave: v })} disabled={ro} />
        <Row label="Rôles requis pour ouvrir" help="Vide : tout le monde"><RolePicker value={s.requiredRoles} onChange={(v) => set({ ...s, requiredRoles: v })} disabled={ro} roles={roles} max={25} /></Row>
        <Row label="Rôles interdits"><RolePicker value={s.forbiddenRoles} onChange={(v) => set({ ...s, forbiddenRoles: v })} disabled={ro} roles={roles} max={25} /></Row>
      </Panel>

      <Panel title="Publier" icon={Send}>
        <p className="text-xs text-text-muted">Le bot publie le panel dans le salon choisi, ou met à jour celui qu'il a déjà publié.</p>
        {dirty && <p className="text-xs text-amber-200">Enregistre d'abord tes changements (barre en bas).</p>}
        <Btn variant="primary" disabled={!canAct || dirty || !s.channelId} onClick={() => run("ticket_panel", {}, "Le bot publie le panel")} testId="ticket-publish"><Send className="w-4 h-4" />Publier / mettre à jour le panel</Btn>
        <ActionHistory actions={actions} type="ticket_panel" />
      </Panel>
    </div>
  );
}

// ── Menus de rôles : embed + boutons ou menu déroulant qui donnent / retirent des rôles ──

const MENU_ACTIONS = [
  { value: "toggle", label: "🔁 Clic = donne le rôle, reclic = le retire" },
  { value: "add", label: "➕ Donne seulement (le membre ne peut pas le retirer)" },
  { value: "remove", label: "➖ Retire les rôles" },
];
const BUTTON_COLORS = [
  { value: "primary", label: "Bleu", cls: "bg-[#5865f2]" },
  { value: "secondary", label: "Gris", cls: "bg-[#4e5058]" },
  { value: "success", label: "Vert", cls: "bg-[#248046]" },
  { value: "danger", label: "Rouge", cls: "bg-[#da373c]" },
];
const MENU_PRESETS = [
  {
    key: "rules", label: "Règlement à accepter", hint: "Un bouton « J'accepte » qui donne l'accès au serveur",
    menu: { name: "Règlement", title: "📜 Règlement", text: "Lis le règlement puis clique sur « J'accepte » pour accéder au serveur.", color: "#22c55e",
      options: [{ label: "J'accepte le règlement", emoji: "✅", style: "success", action: "add", hideFromOthers: true, message: "Bienvenue sur le serveur !" }] },
  },
  {
    key: "colors", label: "Couleur de pseudo", hint: "Menu déroulant, une seule couleur à la fois",
    menu: { name: "Couleurs", style: "select", exclusive: true, resetButton: true, title: "🎨 Couleur de ton pseudo", text: "Choisis une couleur dans le menu.", placeholder: "Choisis ta couleur",
      options: [{ label: "Rouge", emoji: "🔴" }, { label: "Bleu", emoji: "🔵" }, { label: "Vert", emoji: "🟢" }] },
  },
  {
    key: "pings", label: "Notifications", hint: "Des boutons à cocher / décocher",
    menu: { name: "Notifications", title: "🔔 Notifications", text: "Clique pour recevoir (ou ne plus recevoir) les annonces.", resetButton: true,
      options: [{ label: "Annonces", emoji: "📢" }, { label: "Événements", emoji: "🎉" }, { label: "Giveaways", emoji: "🎁" }] },
  },
  { key: "empty", label: "Menu vide", hint: "Tout configurer soi-même", menu: { options: [] } },
];

function ChannelMultiPicker({ value, onChange, disabled, channels, testId }) {
  const byId = Object.fromEntries(channels.map((c) => [c.id, c]));
  const [manual, setManual] = useState("");
  const cats = channels.filter((c) => c.type === 4 && !value.includes(c.id));
  const others = channels.filter((c) => c.type !== 4 && !value.includes(c.id));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 && <span className="text-xs text-text-muted">Aucun</span>}
        {value.map((id) => (
          <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-md bg-white/5 border border-white/10">
            {byId[id] ? <>{(() => { const I = channelIcon(byId[id]); return <I className="w-3.5 h-3.5 text-text-muted" />; })()}{byId[id].name}</> : `Salon supprimé (${id})`}
            {!disabled && <button type="button" onClick={() => onChange(value.filter((v) => v !== id))} aria-label="Retirer" className="text-text-muted hover:text-white"><X className="w-3 h-3" /></button>}
          </span>
        ))}
      </div>
      {!disabled && value.length < 25 && (channels.length ? (
        <Picker value="" onChange={(v) => v && onChange([...value, v])} testId={testId} trigger={<><Plus className="w-3.5 h-3.5" />Ajouter un salon ou une catégorie</>}
          searchPlaceholder="Rechercher un salon ou une catégorie"
          options={[...cats.map((c) => ({ value: c.id, label: c.name, group: "Catégories (tous leurs salons)", icon: Folder })),
            ...others.map((c) => ({ value: c.id, label: c.name, group: c.category || "Sans catégorie", icon: channelIcon(c) }))]} />
      ) : (
        <div className="flex gap-2">
          <TextInput value={manual} onChange={setManual} placeholder="ID du salon ou de la catégorie" />
          <Btn onClick={() => { if (/^\d{15,25}$/.test(manual)) { onChange([...value, manual]); setManual(""); } }}><Plus className="w-4 h-4" /></Btn>
        </div>
      ))}
    </div>
  );
}

function RoleMenuOption({ o, i, count, menu, update, remove, move, ro, channels, roles }) {
  const [open, setOpen] = useState(!o.roles?.length && !o.removeRoles?.length);
  const set = (patch) => update(i, { ...o, ...patch });
  const isRemove = o.action === "remove";
  const missing = !o.roles.length && !o.removeRoles.length;
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="rolemenu-option">
      <div className="flex items-center gap-2 p-2.5 bg-white/[0.03]">
        <button type="button" className="flex-1 min-w-0 text-left text-sm flex items-center gap-2" onClick={() => setOpen(!open)}>
          <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
          {menu.style === "buttons" && <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${(BUTTON_COLORS.find((c) => c.value === o.style) || BUTTON_COLORS[0]).cls}`} />}
          <span>{o.emoji}</span><b className="truncate">{o.label || "Sans nom"}</b>
          <span className="text-[11px] text-text-muted truncate hidden sm:inline">{MENU_ACTIONS.find((a) => a.value === o.action)?.label.slice(0, 2)} {o.roles.length + o.removeRoles.length} rôle(s){o.reveal.length ? ` · ${o.reveal.length} salon(s)` : ""}</span>
          {missing && <span className="text-[11px] text-amber-300 shrink-0">choisis un rôle</span>}
        </button>
        {!ro && (
          <>
            <button type="button" aria-label="Monter" disabled={i === 0} onClick={() => move(i, -1)} className="text-text-muted hover:text-white disabled:opacity-30 px-1">↑</button>
            <button type="button" aria-label="Descendre" disabled={i === count - 1} onClick={() => move(i, 1)} className="text-text-muted hover:text-white disabled:opacity-30 px-1">↓</button>
            <button type="button" aria-label="Supprimer ce choix" onClick={() => remove(i)} className="text-text-muted hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
      {open && (
        <div className="p-3 space-y-3">
          <div className="grid sm:grid-cols-[1fr_110px] gap-2">
            <TextInput value={o.label} onChange={(v) => set({ label: v })} disabled={ro} placeholder="Texte du bouton (ex. Annonces)" maxLength={80} testId={`rolemenu-opt-label-${i}`} />
            <TextInput value={o.emoji} onChange={(v) => set({ emoji: v })} disabled={ro} placeholder="Emoji" maxLength={64} />
          </div>
          {menu.style === "buttons" ? (
            <Row label="Couleur du bouton">
              <div className="flex flex-wrap gap-1.5">
                {BUTTON_COLORS.map((c) => (
                  <button key={c.value} type="button" disabled={ro} onClick={() => set({ style: c.value })}
                    className={`px-3 py-1 rounded text-xs text-white ${c.cls} ${o.style === c.value ? "ring-2 ring-white" : "opacity-60 hover:opacity-100"}`}>{c.label}</button>
                ))}
              </div>
            </Row>
          ) : (
            <TextInput value={o.description} onChange={(v) => set({ description: v })} disabled={ro} placeholder="Description sous le choix (facultatif)" maxLength={100} />
          )}
          <Row label="Quand on clique"><Select value={o.action} disabled={ro} onChange={(v) => set({ action: v, ...(v === "remove" ? { reveal: [], hideFromOthers: false } : {}) })} options={MENU_ACTIONS} testId={`rolemenu-opt-action-${i}`} /></Row>
          <Row label={isRemove ? "Rôles retirés" : "Rôles donnés"} help={isRemove ? "Ex. tous les rôles de couleur" : "10 maximum"}>
            <RolePicker value={o.roles} onChange={(v) => set({ roles: v })} disabled={ro} roles={roles} max={10} testId={`rolemenu-opt-roles-${i}`} />
          </Row>
          {!isRemove && (
            <Row label="Rôles retirés en même temps" help="Ex. « Non vérifié » quand on accepte le règlement">
              <RolePicker value={o.removeRoles} onChange={(v) => set({ removeRoles: v })} disabled={ro} roles={roles} max={10} />
            </Row>
          )}
          {!isRemove && (
            <div className="rounded-lg border border-white/5 bg-black/20 p-3 space-y-3">
              <Row label="Salons qui apparaissent" help="Le bot rend ces salons / catégories visibles pour les rôles donnés (à la publication)">
                <ChannelMultiPicker value={o.reveal} onChange={(v) => set({ reveal: v, hideFromOthers: v.length ? o.hideFromOthers : false })} disabled={ro || !o.roles.length} channels={channels} testId={`rolemenu-opt-reveal-${i}`} />
              </Row>
              {!o.roles.length && <p className="text-[11px] text-text-muted">Choisis d'abord un rôle donné.</p>}
              {o.reveal.length > 0 && (
                <SwitchRow label="Cacher ces salons aux autres" help="Seuls ceux qui ont le rôle les voient (règlement, accès au serveur…)" checked={o.hideFromOthers} onChange={(v) => set({ hideFromOthers: v })} disabled={ro} testId={`rolemenu-opt-hide-${i}`} />
              )}
            </div>
          )}
          <Row label="Message de confirmation" help="Vide : la liste des rôles ajoutés / retirés"><TextInput value={o.message} onChange={(v) => set({ message: v })} disabled={ro} maxLength={500} placeholder="Ex. Bienvenue sur le serveur !" /></Row>
        </div>
      )}
    </div>
  );
}

function RoleMenuPreview({ menu }) {
  const color = (c) => (BUTTON_COLORS.find((b) => b.value === c) || BUTTON_COLORS[0]).cls;
  return (
    <DiscordPreview message={{ content: "", embeds: [{ title: menu.title, description: menu.text || (menu.title ? "" : "🎭"), color: menu.color, image: menu.image }], buttons: [] }}
      footer={(
        <div className="flex flex-wrap gap-1.5 pt-1 max-w-[520px]">
          {menu.style === "select" ? (
            <div className="w-full bg-[#1e1f22] text-sm text-[#949ba4] px-3 py-2 rounded flex items-center justify-between">
              {menu.placeholder || "Choisis tes rôles"}<ChevronDown className="w-4 h-4" />
            </div>
          ) : menu.options.map((o, i) => (
            <span key={i} className={`text-sm px-3 py-1 rounded text-white ${color(o.style)}`}>{o.emoji ? `${o.emoji} ` : ""}{o.label || "Sans nom"}</span>
          ))}
          {menu.resetButton && <span className="text-sm px-3 py-1 rounded text-white bg-[#4e5058]">🗑️ Retirer mes rôles</span>}
        </div>
      )} />
  );
}

function RoleMenusTab({ s, set, ro, channels, roles, meta, botId, guildId, dirty, canAct }) {
  const { actions, run } = useActions(botId, guildId);
  const [selected, setSelected] = useState(0);
  const [picking, setPicking] = useState(false);
  const menus = s.menus || [];
  const max = meta?.max_role_menus || 10;
  const menuDefault = meta?.role_menu_default || {};
  const optionDefault = meta?.role_menu_option_default || {};
  const current = menus[Math.min(selected, menus.length - 1)];
  const idx = Math.min(selected, menus.length - 1);
  const setMenu = (m) => set({ ...s, menus: menus.map((x, j) => (j === idx ? m : x)) });
  const patch = (p) => setMenu({ ...current, ...p });
  const addMenu = (preset) => {
    const { options = [], ...rest } = preset.menu;
    const menu = { ...clone(menuDefault), ...rest, id: "", options: options.map((o) => ({ ...clone(optionDefault), ...o, id: "" })) };
    set({ ...s, menus: [...menus, menu] });
    setSelected(menus.length);
    setPicking(false);
  };
  const removeMenu = () => {
    if (!window.confirm(`Supprimer le menu « ${current.name} » ? Son message sur Discord sera supprimé après l'enregistrement.`)) return;
    set({ ...s, menus: menus.filter((_, j) => j !== idx) });
    setSelected(Math.max(0, idx - 1));
  };
  const updateOpt = (i, o) => patch({ options: current.options.map((x, j) => (j === i ? o : x)) });
  const removeOpt = (i) => patch({ options: current.options.filter((_, j) => j !== i) });
  const moveOpt = (i, d) => {
    const list = [...current.options];
    [list[i], list[i + d]] = [list[i + d], list[i]];
    patch({ options: list });
  };
  const optLimit = current && current.style === "buttons" && current.resetButton ? 24 : 25;
  const channelName = (id) => channels.find((c) => c.id === id)?.name;
  const menuActions = current?.id ? actions.filter((a) => a.type === "role_menu" && a.summary.includes(`« ${current.name} »`)) : [];

  return (
    <div className="space-y-4">
      <Panel title={`Menus de rôles (${menus.length}/${max})`} icon={Tags} actions={!ro && menus.length < max && (
        <Btn onClick={() => setPicking(!picking)} testId="rolemenu-new"><Plus className="w-4 h-4" />Nouveau menu</Btn>
      )}>
        <p className="text-xs text-text-muted">Le bot publie un embed avec des boutons ou un menu déroulant. Chaque choix donne ou retire des rôles, et peut faire apparaître des salons (règlement, couleurs, notifications…).</p>
        {picking && (
          <div className="grid sm:grid-cols-2 gap-2" data-testid="rolemenu-presets">
            {MENU_PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => addMenu(p)} data-testid={`rolemenu-preset-${p.key}`}
                className="text-left rounded-lg border border-white/10 hover:border-blue-500/50 bg-white/[0.03] p-3">
                <div className="text-sm font-medium">{p.label}</div>
                <div className="text-xs text-text-muted">{p.hint}</div>
              </button>
            ))}
          </div>
        )}
        {menus.length === 0 && !picking && <p className="text-sm text-text-muted">Aucun menu pour l'instant.</p>}
        {menus.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="rolemenu-list">
            {menus.map((m, j) => (
              <button key={m.id || `new-${j}`} type="button" onClick={() => setSelected(j)} data-testid={`rolemenu-tab-${j}`}
                className={`text-left rounded-lg border px-3 py-2 min-w-[140px] transition-colors ${j === idx ? "border-blue-500/60 bg-blue-500/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
                <div className="text-sm font-medium truncate max-w-[200px]">{m.name || "Sans nom"}</div>
                <div className="text-[11px] text-text-muted">{m.options.length} choix · {m.channelId ? `#${channelName(m.channelId) || "salon"}` : "pas de salon"}{m.id ? "" : " · non enregistré"}</div>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {current && (
        <>
          <Panel title={`Menu « ${current.name || "Sans nom"} »`} icon={Settings2} actions={!ro && (
            <Btn variant="danger" onClick={removeMenu} testId="rolemenu-delete"><Trash2 className="w-4 h-4" />Supprimer</Btn>
          )}>
            <Row label="Nom du menu" help="Pour t'y retrouver (pas affiché sur Discord)"><TextInput value={current.name} onChange={(v) => patch({ name: v })} disabled={ro} maxLength={60} testId="rolemenu-name" /></Row>
            <Row label="Salon" help="Où le bot publie le menu"><ChannelSelect value={current.channelId} onChange={(v) => patch({ channelId: v })} disabled={ro} channels={channels} emptyLabel="Choisir un salon" testId="rolemenu-channel" /></Row>
            <Row label="Présentation">
              <Select value={current.style} disabled={ro} onChange={(v) => patch({ style: v })} testId="rolemenu-style"
                options={[{ value: "buttons", label: "🔘 Un bouton par choix" }, { value: "select", label: "📋 Menu déroulant" }]} />
            </Row>
            {current.style === "select" && (
              <Row label="Texte du menu déroulant"><TextInput value={current.placeholder} onChange={(v) => patch({ placeholder: v })} disabled={ro} maxLength={150} /></Row>
            )}
            <SwitchRow label="Un seul choix à la fois" help="Choisir une option retire les rôles des autres (ex. couleurs)" checked={current.exclusive} onChange={(v) => patch({ exclusive: v })} disabled={ro} testId="rolemenu-exclusive" />
            <SwitchRow label="Bouton « Retirer mes rôles »" help="Retire d'un coup les rôles du menu qu'on peut enlever" checked={current.resetButton} onChange={(v) => patch({ resetButton: v })} disabled={ro} testId="rolemenu-reset" />
            <Row label="Qui peut l'utiliser" help="Vide : tout le monde"><RolePicker value={current.requiredRoles} onChange={(v) => patch({ requiredRoles: v })} disabled={ro} roles={roles} max={25} /></Row>
          </Panel>

          <Panel title="Embed" icon={MessageSquare}>
            <Row label="Titre" help="Vide : pas de titre"><TextInput value={current.title} onChange={(v) => patch({ title: v })} disabled={ro} maxLength={256} testId="rolemenu-title" /></Row>
            <Row label="Texte"><TextArea value={current.text} onChange={(v) => patch({ text: v })} disabled={ro} maxLength={4000} rows={4} testId="rolemenu-text" /></Row>
            <Row label="Couleur"><ColorInput value={current.color} onChange={(v) => patch({ color: v })} disabled={ro} /></Row>
            <Row label="Image / bannière" help="Facultatif : envoie une image, colle un lien, ou laisse vide"><ImageField value={current.image} onChange={(v) => patch({ image: v })} disabled={ro} testId="rolemenu-image" /></Row>
            <div>
              <div className="text-xs text-text-muted mb-1.5">Aperçu</div>
              <RoleMenuPreview menu={current} />
            </div>
          </Panel>

          <Panel title={`Choix (${current.options.length}/${optLimit})`} icon={Tags} actions={!ro && current.options.length < optLimit && (
            <Btn onClick={() => patch({ options: [...current.options, { ...clone(optionDefault), id: "", label: `Rôle ${current.options.length + 1}` }] })} testId="rolemenu-add-option"><Plus className="w-4 h-4" />Ajouter</Btn>
          )}>
            {current.options.length === 0 && <p className="text-sm text-text-muted">Ajoute au moins un choix (un bouton ou une ligne du menu).</p>}
            {current.options.map((o, i) => (
              <RoleMenuOption key={`${current.id}-${o.id || i}`} o={o} i={i} count={current.options.length} menu={current}
                update={updateOpt} remove={removeOpt} move={moveOpt} ro={ro} channels={channels} roles={roles} />
            ))}
          </Panel>

          <Panel title="Publier" icon={Send}>
            <p className="text-xs text-text-muted">Le bot publie le menu dans le salon choisi, ou met à jour celui qu'il a déjà publié. Les salons à faire apparaître sont réglés à ce moment-là.</p>
            {dirty && <p className="text-xs text-amber-200">Enregistre d'abord tes changements (barre en bas).</p>}
            <Btn variant="primary" disabled={!canAct || dirty || !current.id || !current.channelId || !current.options.length}
              onClick={() => run("role_menu", { menu_id: current.id }, "Le bot publie le menu")} testId="rolemenu-publish">
              <Send className="w-4 h-4" />Publier / mettre à jour le menu
            </Btn>
            <ActionHistory actions={menuActions} type="role_menu" />
          </Panel>
        </>
      )}
    </div>
  );
}

// ── Bot radio : une radio par salon vocal ──

function VoiceSelect({ value, onChange, disabled, channels, taken, testId }) {
  const options = useMemo(() => channels.filter((c) => c.type === 2 || c.type === 13)
    .map((c) => ({ value: c.id, label: c.name, group: c.category || "Sans catégorie", icon: channelIcon(c), disabled: taken.includes(c.id) && c.id !== value, hint: taken.includes(c.id) && c.id !== value ? "déjà une radio" : "" })), [channels, taken, value]);
  if (!channels.length) return <TextInput value={value} onChange={onChange} disabled={disabled} placeholder="ID du salon vocal" testId={testId} />;
  return <Picker value={value} options={options} onChange={onChange} disabled={disabled} testId={testId} placeholder="Choisir un salon vocal" searchPlaceholder="Rechercher un salon vocal" />;
}

const SOURCE_NAMES = { youtube: "YouTube", spotify: "Spotify", deezer: "Deezer", apple: "Apple Music", soundcloud: "SoundCloud", search: "Recherche YouTube", radio: "Radio" };
const fmtDuration = (s) => { const t = Math.max(0, Math.round(s || 0)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`; };

// Ce que joue le bot (radio ou musique), relu toutes les 5 s.
function useLive(botId, guildId, initial) {
  const [live, setLive] = useState(initial || null);
  const load = useCallback(() => dash.get(`/bots/${botId}/guilds/${guildId}/live`).then((r) => setLive(r.live)).catch(() => {}), [botId, guildId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  return [live, load];
}

function NowPlayingCard({ live, channelName, canAct, run }) {
  if (!live) {
    return (
      <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="now-playing-empty">
        <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Radio className="w-7 h-7 text-white/30" /></div>
        <div className="text-sm text-text-muted">Rien en lecture pour l'instant. Lance une radio ou une musique ci-dessous.</div>
      </div>
    );
  }
  const isRadio = live.kind === "radio";
  return (
    <div className="space-y-3" data-testid="now-playing">
      <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-gradient-to-r from-white/[0.05] to-transparent p-4">
        {live.thumbnail
          ? <img src={live.thumbnail} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0 shadow-lg" />
          : <div className={`w-20 h-20 rounded-lg flex items-center justify-center shrink-0 ${isRadio ? "bg-orange-500/15" : "bg-emerald-500/15"}`}><Radio className={`w-8 h-8 ${isRadio ? "text-orange-300" : "text-emerald-300"}`} /></div>}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className={`px-1.5 py-0.5 rounded border ${isRadio ? "border-orange-500/30 bg-orange-500/10 text-orange-200" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"}`}>
              {isRadio ? `📻 ${live.stationName || "Radio"} · en direct` : `🎵 ${SOURCE_NAMES[live.source] || "Musique"}`}
            </span>
            {live.channelId && <span className="text-text-muted">🔊 {channelName(live.channelId)}</span>}
          </div>
          <div className="text-lg font-semibold leading-tight mt-1 truncate" data-testid="now-playing-title">{live.url ? <a href={live.url} target="_blank" rel="noreferrer" className="hover:underline">{live.title || "Titre inconnu"}</a> : (live.title || "Titre inconnu")}</div>
          {live.artist && <div className="text-sm text-white/70 truncate" data-testid="now-playing-artist">{live.artist}</div>}
          <div className="text-[11px] text-text-muted mt-0.5">
            {!isRadio && live.duration ? `${fmtDuration(live.duration)} · ` : ""}{live.requestedBy ? `demandé par ${live.requestedBy.replace(/^<@!?(\d+)>$/, "un membre")}` : isRadio ? "titre lu dans le flux de la radio" : ""}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {!isRadio && <Btn disabled={!canAct} onClick={() => run("music_skip", {}, "Titre suivant")} testId="music-skip" title="Titre suivant"><ChevronRight className="w-4 h-4" /><span className="hidden sm:inline">Suivant</span></Btn>}
          <Btn variant="danger" disabled={!canAct} onClick={() => run("radio_stop", {}, "Le bot arrête la lecture")} testId="radio-stop" title="Tout arrêter"><X className="w-4 h-4" /><span className="hidden sm:inline">Arrêter</span></Btn>
        </div>
      </div>
      {!isRadio && live.queueLength > 0 && (
        <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="music-queue">
          <div className="flex items-center justify-between px-3 py-2 bg-white/[0.03] text-xs">
            <span>À suivre ({live.queueLength})</span>
            <button type="button" disabled={!canAct} onClick={() => run("music_clear", {}, "File vidée")} className="text-text-muted hover:text-red-300 disabled:opacity-40" data-testid="music-clear">Vider la file</button>
          </div>
          <ol className="divide-y divide-white/5 text-sm">
            {live.queue.map((t, i) => <li key={i} className="px-3 py-1.5 truncate"><span className="text-text-muted tabular-nums mr-2">{i + 1}.</span>{t.title}{t.artist && <span className="text-text-muted"> — {t.artist}</span>}</li>)}
            {live.queueLength > live.queue.length && <li className="px-3 py-1.5 text-xs text-text-muted">… et {live.queueLength - live.queue.length} autre(s)</li>}
          </ol>
        </div>
      )}
    </div>
  );
}

const AUDIO_ACCEPT = ".mp3,.ogg,.opus,.wav,.flac,.m4a,.aac,.webm,audio/*";
const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

/** Musiques envoyées depuis l'ordinateur (MP3…) : le bot les joue sans passer par YouTube. */
function AudioLibrary({ botId, guildId, channelId, volume, canAct, run, onDone }) {
  const [files, setFiles] = useState([]);
  const [maxSize, setMaxSize] = useState(40 * 1024 * 1024);
  const [progress, setProgress] = useState(null);
  const input = useRef(null);
  const base = `/bots/${botId}/guilds/${guildId}/audio`;
  const load = useCallback(() => dash.get(base).then((r) => { setFiles(r.files); setMaxSize(r.max_size); }).catch(() => {}), [base]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (file.size > maxSize) { toast.error(`Fichier trop lourd (max ${fmtSize(maxSize)})`); return; }
    setProgress(0);
    try {
      const start = await dash.post(base, { filename: file.name, size: file.size });
      for (let n = 0; n < start.chunks; n += 1) {
        const part = file.slice(n * start.chunk_size, Math.min(file.size, (n + 1) * start.chunk_size));
        await axios.put(`${API}/dash${base}/${start.id}/chunks/${n}`, part, { headers: { ...auth().headers, "Content-Type": "application/octet-stream" } });
        setProgress(Math.round(((n + 1) / start.chunks) * 100));
      }
      const done = await dash.post(`${base}/${start.id}/complete`);
      setFiles((list) => [done.file, ...list]);
      toast.success(`« ${done.file.title} » envoyé : clique sur Lire`);
    } catch (err) { toast.error(errorText(err, "Envoi impossible")); }
    setProgress(null);
  };
  const pick = (e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) upload(file); };
  const playFile = async (f) => {
    const ok = await run("music_play", { channel_id: channelId, audio_id: f.id, volume }, `Le bot lance « ${f.title} »`);
    if (ok) onDone();
  };
  const remove = async (f) => {
    try { await dash.del(`${base}/${f.id}`); setFiles((list) => list.filter((x) => x.id !== f.id)); } catch (err) { toast.error(errorText(err, "Suppression impossible")); }
  };
  return (
    <div className="space-y-2 pt-2 border-t border-white/5" data-testid="audio-library">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium flex-1 min-w-[200px]">Mes musiques <span className="text-xs text-text-muted font-normal">— envoie un MP3 (ou OGG, WAV, FLAC, M4A) depuis ton ordinateur : le bot le joue en entier, sans YouTube.</span></span>
        <input ref={input} type="file" accept={AUDIO_ACCEPT} className="hidden" onChange={pick} data-testid="audio-input" />
        <Btn disabled={!canAct || progress !== null} onClick={() => input.current?.click()} testId="audio-upload">
          {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin" />{progress} %</> : <><Upload className="w-4 h-4" />Envoyer un MP3</>}
        </Btn>
      </div>
      {files.length === 0 && <p className="text-xs text-text-muted">Aucune musique envoyée pour l'instant (max {fmtSize(maxSize)} par fichier).</p>}
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2" data-testid="audio-file">
          <span className="flex-1 min-w-0 truncate text-sm">🎵 {f.title}</span>
          <span className="text-xs text-text-muted tabular-nums">{fmtSize(f.size)}</span>
          <Btn variant="primary" disabled={!canAct || !channelId} onClick={() => playFile(f)} testId="audio-play"><Send className="w-4 h-4" />Lire</Btn>
          <button type="button" aria-label={`Supprimer ${f.title}`} disabled={!canAct} onClick={() => remove(f)} className="p-2 rounded-lg text-text-muted hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function MusicPanel({ channels, salons, canAct, run, onDone, botId, guildId }) {
  const voice = channels.filter((c) => c.type === 2 || c.type === 13);
  const [channelId, setChannelId] = useState(salons[0]?.channelId || voice[0]?.id || "");
  const [query, setQuery] = useState("");
  const [volume, setVolume] = useState(80);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  // Suggestions pendant qu'on écrit, comme sur Spotify (« Bella » → les titres et leurs artistes).
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || /^https?:\/\//i.test(q)) { setSuggestions([]); return undefined; }
    const timer = setTimeout(() => {
      dash.get(`/music/search?q=${encodeURIComponent(q)}`).then((r) => setSuggestions(r.results || [])).catch(() => setSuggestions([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);
  const play = async (pick) => {
    setBusy(true);
    setShowSuggest(false);
    const what = pick ? pick.url : query.trim();
    const ok = await run("music_play", { channel_id: channelId, query: what, volume }, pick ? `Le bot lance « ${pick.title} » de ${pick.artist}` : "Le bot lance la musique");
    setBusy(false);
    if (ok) { setQuery(""); setSuggestions([]); onDone(); }
  };
  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  return (
    <Panel title="Musique" icon={Sparkles}>
      <p className="text-xs text-text-muted">Colle un lien <b>YouTube</b>, <b>Spotify</b>, <b>Deezer</b>, <b>Apple Music</b> (titre, album ou playlist), un <b>lien direct vers un MP3</b> (https://…/musique.mp3), ou écris le nom d'une musique. Si une musique joue déjà, elle est ajoutée à la file.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative" onFocus={() => setShowSuggest(true)} onBlur={() => setTimeout(() => setShowSuggest(false), 200)}>
          <TextInput value={query} onChange={(v) => { setQuery(v); setShowSuggest(true); }} placeholder="Écris un titre (ex. Bella) ou colle un lien Spotify / YouTube / MP3" maxLength={500} testId="music-query" />
          {showSuggest && suggestions.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-lg border border-white/10 bg-[#16161f] shadow-xl" data-testid="music-suggestions">
              {suggestions.map((t) => (
                <button key={t.url} type="button" disabled={!canAct || !channelId || busy} onMouseDown={(e) => e.preventDefault()} onClick={() => play(t)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/5 disabled:opacity-50" data-testid="music-suggestion">
                  {t.cover ? <img src={t.cover} alt="" className="w-10 h-10 rounded object-cover shrink-0" /> : <div className="w-10 h-10 rounded bg-white/5 shrink-0" />}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm truncate">{t.title}</span>
                    <span className="block text-xs text-text-muted truncate">{t.artist}</span>
                  </span>
                  {t.duration > 0 && <span className="text-xs text-text-muted tabular-nums">{mmss(t.duration)}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <Btn variant="primary" disabled={!canAct || busy || !query.trim() || !channelId} onClick={() => play()} testId="music-play"><Send className="w-4 h-4" />Lire</Btn>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 items-center">
        <VoiceSelect value={channelId} onChange={setChannelId} channels={channels} taken={[]} testId="music-channel" />
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">Volume</span>
          <input type="range" min={1} max={100} value={volume} onChange={(e) => setVolume(Number(e.target.value))} className="flex-1 accent-blue-500" aria-label="Volume de la musique" />
          <span className="text-sm w-12 text-right tabular-nums">{volume} %</span>
        </div>
      </div>
      <AudioLibrary botId={botId} guildId={guildId} channelId={channelId} volume={volume} canAct={canAct} run={run} onDone={onDone} />
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-white/5">
        <span className="text-xs text-text-muted flex-1 min-w-[200px]">Un lien ne se lit pas ? Le test vérifie YouTube et les outils du bot (yt-dlp, FFmpeg…), et dit ce qui bloque (résultat dans l'historique ci-dessous).</span>
        <Btn disabled={!canAct} onClick={() => run("music_test", {}, "Test lancé : résultat dans l'historique d'ici 1 minute")} testId="music-test"><Activity className="w-4 h-4" />Tester la musique</Btn>
      </div>
    </Panel>
  );
}

function RadioTab({ s, set, ro, channels, meta, botId, guildId, dirty, canAct, online, initialLive }) {
  const { actions, run } = useActions(botId, guildId);
  const [live, reloadLive] = useLive(botId, guildId, initialLive);
  const stations = meta?.radio_stations || [];
  const names = Object.fromEntries(stations.flatMap((g) => g.stations.map((x) => [x.key, x.name])));
  const max = meta?.max_radio_salons || 25;
  const salons = s.salons || [];
  const update = (i, p) => set({ ...s, salons: salons.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const remove = (i) => set({ ...s, salons: salons.filter((_, j) => j !== i) });
  const add = () => set({ ...s, salons: [...salons, { channelId: "", station: "skyrock", volume: 80 }] });
  const channelName = (id) => channels.find((c) => c.id === id)?.name || id;
  const act = async (type, payload, text) => { const ok = await run(type, payload, text); if (ok) setTimeout(reloadLive, 4000); return ok; };
  const radioActions = actions.filter((a) => ["radio_play", "radio_stop", "music_play", "music_skip", "music_clear", "music_test"].includes(a.type));
  return (
    <div className="space-y-4">
      <Panel title="En ce moment" icon={Activity}>
        <NowPlayingCard live={live} channelName={channelName} canAct={canAct} run={act} />
        {!online && <p className="text-xs text-amber-200">Le bot n'est pas connecté au site : les actions seront faites dès qu'il se connecte.</p>}
      </Panel>

      <MusicPanel channels={channels} salons={salons} canAct={canAct} run={act} onDone={reloadLive} botId={botId} guildId={guildId} />

      <Panel title={`Salons radio (${salons.length}/${max})`} icon={Radio} actions={!ro && salons.length < max && (
        <Btn onClick={add} testId="radio-add"><Plus className="w-4 h-4" />Ajouter un salon</Btn>
      )}>
        <p className="text-xs text-text-muted">Quand quelqu'un rejoint un de ces salons vocaux, le bot le rejoint et lance la radio choisie (sauf si une musique est en cours). Le bot ne peut être que dans un salon à la fois sur un serveur.</p>
        {salons.length === 0 && <p className="text-sm text-text-muted">Aucun salon radio pour l'instant. Ajoute un salon vocal et choisis sa radio.</p>}
        {salons.map((r, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-3" data-testid="radio-salon">
            <div className="grid md:grid-cols-2 gap-2">
              <VoiceSelect value={r.channelId} onChange={(v) => update(i, { channelId: v })} disabled={ro} channels={channels} taken={salons.map((x) => x.channelId)} testId={`radio-channel-${i}`} />
              <Picker value={r.station} disabled={ro} testId={`radio-station-${i}`} onChange={(v) => update(i, { station: v })} searchPlaceholder="Rechercher une radio"
                options={stations.flatMap((g) => g.stations.map((x) => ({ value: x.key, label: x.name, group: g.group, icon: Radio })))} />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-text-muted w-14">Volume</span>
              <input type="range" min={1} max={100} value={r.volume} disabled={ro} onChange={(e) => update(i, { volume: Number(e.target.value) })}
                className="flex-1 min-w-[140px] accent-blue-500" data-testid={`radio-volume-${i}`} aria-label="Volume" />
              <span className="text-sm w-12 text-right tabular-nums">{r.volume} %</span>
              <div className="flex items-center gap-2 ml-auto">
                <Btn variant="primary" disabled={!canAct || dirty || !r.channelId} title={dirty ? "Enregistre d'abord tes changements" : "Le bot rejoint le salon et lance la radio"}
                  onClick={() => act("radio_play", { channel_id: r.channelId }, `Le bot lance ${names[r.station] || "la radio"}`)} testId={`radio-play-${i}`}>
                  <Send className="w-4 h-4" />Lancer maintenant
                </Btn>
                {!ro && <button type="button" aria-label="Retirer ce salon" onClick={() => remove(i)} className="p-2 rounded-lg text-text-muted hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          </div>
        ))}
        <SwitchRow label="Annonce dans le chat du salon" help="Un embed avec la pochette, le titre et l'artiste à chaque musique (et à chaque titre de la radio)"
          checked={s.announce !== false} onChange={(v) => set({ ...s, announce: v })} disabled={ro} testId="radio-announce" />
        {dirty && <p className="text-xs text-amber-200">Enregistre tes changements (barre en bas) pour pouvoir lancer la radio.</p>}
      </Panel>

      <Panel title="Historique" icon={ScrollText}>
        <ActionHistory actions={radioActions} />
        {radioActions.length === 0 && <p className="text-xs text-text-muted">Aucune action pour l'instant.</p>}
      </Panel>
    </div>
  );
}

// ── Messages et embeds ──

const EMPTY_EMBED = { title: "", url: "", description: "", color: "#7c3aed", author: { name: "", icon_url: "", url: "" }, thumbnail: "", image: "", footer: { text: "", icon_url: "" }, timestamp: false, fields: [] };
const EMPTY_MESSAGE = { content: "", embeds: [clone(EMPTY_EMBED)], buttons: [] };

function DiscordPreview({ message, extra, footer }) {
  const botName = "Bot";
  return (
    <div className="bg-[#313338] text-[#dbdee1] p-3 rounded" data-testid="discord-preview">
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-full bg-blue-500/40 flex items-center justify-center shrink-0"><Bot className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-sm"><b className="text-white">{botName}</b> <span className="text-[10px] bg-[#5865f2] text-white px-1 rounded">APP</span></div>
          {message.content && <div className="text-sm whitespace-pre-wrap break-words">{message.content}</div>}
          {(message.embeds || []).map((e, i) => (
            <div key={i} className="max-w-[520px] bg-[#2b2d31] rounded border-l-4 p-3 flex gap-3" style={{ borderColor: e.color || "#1e1f22" }}>
              <div className="min-w-0 flex-1 space-y-1.5">
                {e.author?.name && <div className="text-xs font-semibold text-white flex items-center gap-1.5">{e.author.icon_url && <img src={e.author.icon_url} alt="" className="w-5 h-5 rounded-full" />}{e.author.name}</div>}
                {e.title && <div className={`text-sm font-semibold ${e.url ? "text-[#00a8fc]" : "text-white"}`}>{e.title}</div>}
                {e.description && <div className="text-sm whitespace-pre-wrap break-words">{e.description}</div>}
                {e.fields?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {e.fields.map((f, j) => (
                      <div key={j} className={f.inline ? "col-span-1" : "col-span-3"}>
                        <div className="text-xs font-semibold text-white">{f.name}</div>
                        <div className="text-sm whitespace-pre-wrap break-words">{f.value}</div>
                      </div>
                    ))}
                  </div>
                )}
                {e.image && <img src={e.image} alt="" className="rounded max-h-64 mt-1" />}
                {(e.footer?.text || e.timestamp) && (
                  <div className="text-[11px] text-[#b5bac1] flex items-center gap-1.5">
                    {e.footer?.icon_url && <img src={e.footer.icon_url} alt="" className="w-4 h-4 rounded-full" />}
                    {e.footer?.text}{e.footer?.text && e.timestamp ? " • " : ""}{e.timestamp ? "Aujourd'hui" : ""}
                  </div>
                )}
              </div>
              {e.thumbnail && <img src={e.thumbnail} alt="" className="w-16 h-16 rounded object-cover shrink-0" />}
            </div>
          ))}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {extra === "select" && <div className="w-full max-w-[520px] bg-[#1e1f22] text-sm text-[#949ba4] px-3 py-2 rounded">📋 Choisissez un type de ticket</div>}
            {Array.isArray(extra) && extra.map((b, i) => <span key={`x${i}`} className="text-sm px-3 py-1 rounded bg-[#5865f2] text-white">{b.label}</span>)}
            {(message.buttons || []).map((b, i) => <span key={i} className="text-sm px-3 py-1 rounded bg-[#4e5058] text-white">{b.emoji} {b.label} ↗</span>)}
          </div>
          {footer}
        </div>
      </div>
    </div>
  );
}

function EmbedEditor({ e, n, set, remove }) {
  const patch = (p) => set({ ...e, ...p });
  const field = (i, p) => patch({ fields: e.fields.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  return (
    <div className="border border-white/10 p-3 space-y-3" data-testid="embed-editor">
      <div className="flex items-center gap-2"><b className="text-sm flex-1">Embed {n}</b>{remove && <button type="button" onClick={remove} className="text-text-muted hover:text-red-300" aria-label="Supprimer l'embed"><Trash2 className="w-4 h-4" /></button>}</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center">
        <TextInput value={e.title} onChange={(v) => patch({ title: v })} placeholder="Titre" maxLength={256} testId={`embed-title-${n}`} />
        <ColorInput value={e.color} onChange={(v) => patch({ color: v })} allowEmpty />
      </div>
      <TextArea value={e.description} onChange={(v) => patch({ description: v })} placeholder="Texte (**gras**, *italique*, liens, mentions <@&rôle>…)" maxLength={4096} rows={5} testId={`embed-desc-${n}`} />
      <details className="text-sm">
        <summary className="cursor-pointer text-text-muted">Auteur, images, pied, lien du titre</summary>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          <TextInput value={e.author.name} onChange={(v) => patch({ author: { ...e.author, name: v } })} placeholder="Auteur" maxLength={256} />
          <TextInput value={e.author.icon_url} onChange={(v) => patch({ author: { ...e.author, icon_url: v } })} placeholder="Icône de l'auteur (lien, facultatif)" />
          <TextInput value={e.url} onChange={(v) => patch({ url: v })} placeholder="Lien du titre (facultatif)" />
          <div className="sm:col-span-2 space-y-1"><div className="text-xs text-text-muted">Grande image / bannière (facultatif)</div><ImageField value={e.image} onChange={(v) => patch({ image: v })} testId={`embed-image-${n}`} /></div>
          <div className="sm:col-span-2 space-y-1"><div className="text-xs text-text-muted">Miniature en haut à droite (facultatif)</div><ImageField value={e.thumbnail} onChange={(v) => patch({ thumbnail: v })} /></div>
          <TextInput value={e.footer.text} onChange={(v) => patch({ footer: { ...e.footer, text: v } })} placeholder="Pied de page" maxLength={2048} />
          <TextInput value={e.footer.icon_url} onChange={(v) => patch({ footer: { ...e.footer, icon_url: v } })} placeholder="Icône du pied (lien, facultatif)" />
          <label className="flex items-center gap-2 text-sm"><Toggle checked={e.timestamp} onChange={(v) => patch({ timestamp: v })} label="Date" />Afficher la date</label>
        </div>
      </details>
      <div className="space-y-2">
        {e.fields.map((f, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto_auto] gap-2 items-center">
            <TextInput value={f.name} onChange={(v) => field(i, { name: v })} placeholder="Titre du champ" maxLength={256} />
            <TextInput value={f.value} onChange={(v) => field(i, { value: v })} placeholder="Texte du champ" maxLength={1024} />
            <label className="flex items-center gap-1.5 text-xs"><Toggle checked={f.inline} onChange={(v) => field(i, { inline: v })} label="En ligne" />En ligne</label>
            <button type="button" aria-label="Supprimer le champ" onClick={() => patch({ fields: e.fields.filter((_, j) => j !== i) })} className="text-text-muted hover:text-red-300"><X className="w-4 h-4" /></button>
          </div>
        ))}
        {e.fields.length < 25 && <Btn testId={`field-add-${n}`} onClick={() => patch({ fields: [...e.fields, { name: "", value: "", inline: false }] })}><Plus className="w-4 h-4" />Champ</Btn>}
      </div>
    </div>
  );
}

function EmbedsTab({ botId, guildId, channels, canAct }) {
  const [msg, setMsg] = useState(clone(EMPTY_MESSAGE));
  const [channel, setChannel] = useState("");
  const [editId, setEditId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [current, setCurrent] = useState(null);
  const { actions, run } = useActions(botId, guildId);
  const loadTemplates = useCallback(() => dash.get(`/bots/${botId}/guilds/${guildId}/embeds`).then((r) => setTemplates(r.templates)).catch(() => {}), [botId, guildId]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const setEmbed = (i, e) => setMsg({ ...msg, embeds: msg.embeds.map((x, j) => (j === i ? e : x)) });
  const setButton = (i, p) => setMsg({ ...msg, buttons: msg.buttons.map((b, j) => (j === i ? { ...b, ...p } : b)) });
  // Un embed laissé vide n'est pas envoyé.
  const isEmpty = (e) => !e.title && !e.description && !e.image && !e.thumbnail && !e.author.name && !e.footer.text && !e.fields.length;
  const payload = () => ({ ...msg, embeds: msg.embeds.filter((e) => !isEmpty(e)), buttons: msg.buttons.filter((b) => b.url || b.label) });
  const send = async () => {
    if (!channel) { toast.error("Choisis un salon"); return; }
    const ok = await run("send_message", { ...payload(), channel_id: channel, message_id: editId.trim() }, editId ? "Le bot modifie le message" : "Le bot envoie le message");
    if (ok && !editId) setEditId("");
  };
  const saveTemplate = async () => {
    const name = (tplName || current?.name || "").trim();
    if (!name) { toast.error("Donne un nom au modèle"); return; }
    try {
      // Même nom qu'un modèle existant : il est remplacé.
      const same = templates.find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (same) { await dash.put(`/bots/${botId}/guilds/${guildId}/embeds/${same.id}`, { name, data: payload() }); setCurrent({ ...same, name }); }
      else { const r = await dash.post(`/bots/${botId}/guilds/${guildId}/embeds`, { name, data: payload() }); setCurrent(r.template); }
      toast.success("Modèle enregistré"); loadTemplates();
    } catch (err) { toast.error(errorText(err, "Enregistrement impossible")); }
  };
  const fill = (e) => ({ ...clone(EMPTY_EMBED), ...e, author: { ...EMPTY_EMBED.author, ...(e.author || {}) }, footer: { ...EMPTY_EMBED.footer, ...(e.footer || {}) } });
  const loadTemplate = (t) => {
    setMsg({ content: t.data.content || "", embeds: (t.data.embeds || []).map(fill), buttons: t.data.buttons || [] });
    setCurrent(t); setTplName(t.name);
  };
  const delTemplate = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    await dash.del(`/bots/${botId}/guilds/${guildId}/embeds/${t.id}`).catch(() => {});
    if (current?.id === t.id) setCurrent(null);
    loadTemplates();
  };

  // Embeds déjà envoyés par le bot (+embed sur Discord ou depuis ce site).
  const [sent, setSent] = useState(null);
  const [busyId, setBusyId] = useState("");
  const loadSent = useCallback(() => dash.get(`/bots/${botId}/guilds/${guildId}/sent-messages`).then((r) => setSent(r.messages)).catch(() => setSent((x) => x || [])), [botId, guildId]);
  useEffect(() => { loadSent(); const t = setInterval(() => { if (!document.hidden) loadSent(); }, 10000); return () => clearInterval(t); }, [loadSent]);
  const lastDone = actions.find((a) => a.type === "send_message" && a.status === "done")?.id;
  useEffect(() => { if (lastDone) loadSent(); }, [lastDone, loadSent]);
  const editSent = async (m) => {
    setBusyId(m.message_id);
    try {
      const r = await dash.get(`/bots/${botId}/guilds/${guildId}/sent-messages/${m.message_id}`);
      const data = r.message;
      setMsg({ content: data.content || "", embeds: data.embeds.length ? data.embeds.map(fill) : [clone(EMPTY_EMBED)], buttons: data.buttons || [] });
      setChannel(data.channel_id || m.channel_id);
      setEditId(m.message_id);
      setCurrent(null);
      toast.success("Message chargé : modifie-le puis clique sur « Modifier le message »");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(errorText(err, "Message illisible sur Discord")); }
    setBusyId("");
  };
  const deleteSent = async (m) => {
    if (!window.confirm(`Supprimer « ${m.title || "ce message"} » du salon #${m.channel_name || m.channel_id} ? Il disparaît de Discord.`)) return;
    setBusyId(m.message_id);
    try {
      const r = await dash.del(`/bots/${botId}/guilds/${guildId}/sent-messages/${m.message_id}`);
      setSent(r.messages);
      if (editId === m.message_id) { setEditId(""); }
      toast.success("Message supprimé du salon");
    } catch (err) { toast.error(errorText(err, "Suppression impossible")); }
    setBusyId("");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start">
      <div className="space-y-4">
        {!canAct && <div className="text-sm text-text-muted bg-white/5 border border-white/10 px-3 py-2">Envoyer des messages est réservé à l'acheteur et aux buyers.</div>}
        <Panel title="Message" icon={MessageSquare}>
          <TextArea value={msg.content} onChange={(v) => setMsg({ ...msg, content: v })} placeholder="Texte au-dessus des embeds (facultatif) — @everyone, <@&ID_du_rôle>…" maxLength={2000} testId="msg-content" />
          {msg.embeds.map((e, i) => <EmbedEditor key={i} e={e} n={i + 1} set={(v) => setEmbed(i, v)} remove={msg.embeds.length > 0 ? () => setMsg({ ...msg, embeds: msg.embeds.filter((_, j) => j !== i) }) : null} />)}
          {msg.embeds.length < 10 && <Btn onClick={() => setMsg({ ...msg, embeds: [...msg.embeds, clone(EMPTY_EMBED)] })} testId="embed-add"><Plus className="w-4 h-4" />Ajouter un embed</Btn>}
        </Panel>
        <Panel title={`Boutons-liens (${msg.buttons.length}/25)`} icon={Link2}>
          {msg.buttons.map((b, i) => (
            <div key={i} className="grid sm:grid-cols-[70px_1fr_2fr_auto] gap-2 items-center">
              <TextInput value={b.emoji} onChange={(v) => setButton(i, { emoji: v })} placeholder="🔗" maxLength={64} />
              <TextInput value={b.label} onChange={(v) => setButton(i, { label: v })} placeholder="Texte" maxLength={80} testId={`button-label-${i}`} />
              <TextInput value={b.url} onChange={(v) => setButton(i, { url: v })} placeholder="https://…" testId={`button-url-${i}`} />
              <button type="button" aria-label="Supprimer le bouton" onClick={() => setMsg({ ...msg, buttons: msg.buttons.filter((_, j) => j !== i) })} className="text-text-muted hover:text-red-300"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {msg.buttons.length < 25 && <Btn testId="button-add" onClick={() => setMsg({ ...msg, buttons: [...msg.buttons, { label: "", url: "", emoji: "" }] })}><Plus className="w-4 h-4" />Bouton</Btn>}
        </Panel>
      </div>
      <div className="space-y-4 lg:sticky lg:top-24">
        <Panel title="Aperçu" icon={Eye}><DiscordPreview message={msg} /></Panel>
        <Panel title="Envoyer" icon={Send}>
          <Row label="Salon"><ChannelSelect value={channel} onChange={setChannel} channels={channels} emptyLabel="Choisir un salon" testId="msg-channel" allowEmpty /></Row>
          {editId && sent?.some((m) => m.message_id === editId) && (
            <div className="flex items-center gap-2 rounded-lg bg-blue-500/10 border border-blue-500/30 px-3 py-2 text-xs text-blue-100" data-testid="editing-banner">
              <Pencil className="w-3.5 h-3.5 shrink-0" />Modification de « {sent.find((m) => m.message_id === editId).title || "embed"} »
              <button type="button" className="ml-auto underline" onClick={() => { setEditId(""); setMsg(clone(EMPTY_MESSAGE)); }}>Annuler</button>
            </div>
          )}
          <Row label="Modifier un message du bot" help="Facultatif : ID du message à remplacer"><TextInput value={editId} onChange={setEditId} placeholder="ID du message (clic droit > Copier l'identifiant)" maxLength={25} testId="msg-edit-id" /></Row>
          <Btn variant="primary" onClick={send} disabled={!canAct} testId="msg-send"><Send className="w-4 h-4" />{editId ? "Modifier le message" : "Envoyer dans le salon"}</Btn>
          <ActionHistory actions={actions} type="send_message" />
        </Panel>
        <Panel title={`Messages envoyés${sent ? ` (${sent.length})` : ""}`} icon={MessagesSquare}>
          <p className="text-xs text-text-muted -mt-1">Les embeds envoyés par le bot, ici ou avec <code className="text-blue-300">+embed</code> sur Discord. « Modifier » recharge le message tel qu'il est sur Discord.</p>
          <div className="border border-white/10 rounded-lg divide-y divide-white/5 overflow-hidden" data-testid="sent-list">
            {!sent && <div className="p-3 text-xs text-text-muted">Chargement…</div>}
            {sent && sent.length === 0 && <div className="p-3 text-xs text-text-muted">Aucun embed envoyé pour l'instant.</div>}
            {sent && sent.map((m) => (
              <div key={m.message_id} className={`flex items-center gap-3 p-2.5 ${editId === m.message_id ? "bg-blue-500/10" : ""}`} data-testid="sent-item">
                <span className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><MessageSquare className="w-4 h-4 text-blue-300" /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{m.title || "Embed sans titre"}</div>
                  <div className="text-[11px] text-text-muted truncate">
                    #{m.channel_name || m.channel_id} · {m.source === "site" ? "envoyé depuis le site" : "+embed sur Discord"}{m.by ? ` par ${m.by}` : ""}{m.at ? ` · ${since(m.at)}` : ""}
                  </div>
                </div>
                {canAct && (
                  <div className="flex gap-1 shrink-0">
                    <Btn onClick={() => editSent(m)} disabled={busyId === m.message_id} title="Charger dans l'éditeur" testId={`sent-edit-${m.message_id}`}>{busyId === m.message_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}<span className="hidden xl:inline">Modifier</span></Btn>
                    <Btn variant="danger" onClick={() => deleteSent(m)} disabled={busyId === m.message_id} title="Supprimer du salon" testId={`sent-delete-${m.message_id}`}><Trash2 className="w-4 h-4" /></Btn>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
        <Panel title={`Modèles (${templates.length})`} icon={Save}>
          <div className="flex gap-2">
            <TextInput value={tplName} onChange={setTplName} placeholder="Nom du modèle (ex. Règlement)" maxLength={60} testId="tpl-name" />
            <Btn onClick={saveTemplate} disabled={!canAct} testId="tpl-save"><Save className="w-4 h-4" />Enregistrer</Btn>
          </div>
          <div className="divide-y divide-border-default border border-white/10 rounded-lg overflow-hidden">
            {templates.length === 0 && <div className="p-3 text-xs text-text-muted">Aucun modèle</div>}
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-2 p-2.5 text-sm" data-testid="tpl-item">
                <button type="button" className="flex-1 text-left hover:text-blue-300 truncate" onClick={() => loadTemplate(t)}>{current?.id === t.id ? "▸ " : ""}{t.name}</button>
                <span className="text-[11px] text-text-muted">{since(t.updated_at)}</span>
                {canAct && <button type="button" aria-label="Supprimer le modèle" onClick={() => delTemplate(t)} className="text-text-muted hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
          <Btn onClick={() => { setMsg(clone(EMPTY_MESSAGE)); setCurrent(null); setTplName(""); setEditId(""); }}>Nouveau message vide</Btn>
        </Panel>
      </div>
    </div>
  );
}

// ── Réglages du bot (tous les serveurs) ──

const ACTIVITY_LABELS = { none: "Aucune", playing: "Joue à", streaming: "En stream", listening: "Écoute", watching: "Regarde", competing: "Participe à", custom: "Statut personnalisé" };
const STATUS_LABELS = { online: "🟢 En ligne", idle: "🌙 Absent", dnd: "⛔ Ne pas déranger", invisible: "⚫ Invisible" };

function BotSettingsTab({ botId }) {
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback((keep) => dash.get(`/bots/${botId}/bot-settings`).then((v) => { setView(v); if (!keep) setDraft(clone(v.settings)); }).catch(() => {}), [botId]);
  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    if (!view?.pending) return undefined;
    const t = setInterval(() => load(false), 4000);
    return () => clearInterval(t);
  }, [view, load]);
  if (!view || !draft) return <p className="text-sm text-text-muted">Chargement…</p>;
  const ro = !view.can_edit;
  const dirty = JSON.stringify(draft) !== JSON.stringify(view.settings);
  const act = draft.activity;
  const save = async () => {
    setSaving(true);
    try { const v = await dash.put(`/bots/${botId}/bot-settings`, { values: draft }); setView((old) => ({ ...old, ...v })); setDraft(clone(v.settings)); toast.success("Enregistré : le bot applique les changements"); } catch (err) { toast.error(errorText(err, "Enregistrement impossible")); }
    setSaving(false);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-sky-500/10 border border-sky-500/30 px-3 py-2 text-sm text-sky-100"><Bot className="w-4 h-4 mt-0.5 shrink-0" />Ces réglages s'appliquent au bot sur tous ses serveurs.</div>
      {ro && <div className="flex items-start gap-2 bg-white/5 border border-white/10 px-3 py-2 text-sm text-text-muted" data-testid="readonly-hint"><Shield className="w-4 h-4 mt-0.5 shrink-0" />Lecture seule : réservé à l'acheteur et aux buyers du bot.</div>}
      <SectionStatus pending={view.pending} error={view.errors?.bot} online={view.online} />
      <Panel title="Préfixe" icon={Hash}>
        <Row label="Préfixe des commandes" help="1 à 5 caractères, sans espace (ex. + ! .)"><TextInput value={draft.prefix} onChange={(v) => setDraft({ ...draft, prefix: v.replace(/\s/g, "") })} disabled={ro} maxLength={5} testId="bot-prefix" /></Row>
      </Panel>
      <Panel title="Statut et activité" icon={Activity}>
        <Row label="Statut"><Select value={draft.status} disabled={ro} onChange={(v) => setDraft({ ...draft, status: v })} testId="bot-status" options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))} /></Row>
        <Row label="Activité"><Select value={act.type} disabled={ro} onChange={(v) => setDraft({ ...draft, activity: { ...act, type: v } })} testId="bot-activity-type" options={(view.activity_types || Object.keys(ACTIVITY_LABELS)).map((t) => ({ value: t, label: ACTIVITY_LABELS[t] || t }))} /></Row>
        {act.type !== "none" && <Row label="Texte de l'activité"><TextInput value={act.text} onChange={(v) => setDraft({ ...draft, activity: { ...act, text: v } })} disabled={ro} maxLength={128} placeholder="ex. vanta-pulse.shop" testId="bot-activity-text" /></Row>}
        {act.type === "streaming" && <Row label="Lien du stream" help="twitch.tv ou youtube.com"><TextInput value={act.url} onChange={(v) => setDraft({ ...draft, activity: { ...act, url: v } })} disabled={ro} placeholder="https://www.twitch.tv/…" /></Row>}
        <div className="text-sm text-text-muted">Aperçu : <span className="text-white">{STATUS_LABELS[draft.status]}</span>{act.type !== "none" && act.text && <> · {ACTIVITY_LABELS[act.type]} <b className="text-white">{act.text}</b></>}</div>
      </Panel>
      {!ro && (
        <div className="flex gap-2">
          <Btn onClick={() => setDraft(clone(view.settings))} disabled={!dirty}>Annuler</Btn>
          <Btn variant="primary" onClick={save} disabled={!dirty || saving} testId="bot-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Enregistrer et appliquer</Btn>
        </div>
      )}
      {view.state_at && <p className="text-[11px] text-text-muted">Réglages lus sur le bot {since(view.state_at)}.</p>}
    </div>
  );
}

// ── Système : statut du bot et relance ──

const SYSTEM_STATUS = {
  online: { label: "En ligne", cls: "text-emerald-300", dot: "bg-emerald-400", text: "Le bot fonctionne et répond au site." },
  restarting: { label: "Relance en cours", cls: "text-sky-300", dot: "bg-sky-400", spin: true, text: "Le bot est en train d'être relancé." },
  starting: { label: "Démarrage en cours", cls: "text-amber-300", dot: "bg-amber-400", spin: true, text: "Le bot se connecte à Discord…" },
  offline: { label: "Hors ligne", cls: "text-red-300", dot: "bg-red-400", text: "Le bot ne répond pas en ce moment." },
  error: { label: "Erreur", cls: "text-red-300", dot: "bg-red-400", text: "Le bot n'a pas pu démarrer." },
  stopped: { label: "Arrêté", cls: "text-white/60", dot: "bg-white/40", text: "Le bot n'est pas lancé sur l'hébergement." },
  expired: { label: "Abonnement expiré", cls: "text-amber-300", dot: "bg-amber-400", text: "Renouvelle l'abonnement pour relancer le bot." },
};

const duration = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d) return `${d} j ${h} h`;
  if (h) return `${h} h ${m} min`;
  if (m) return `${m} min`;
  return `${s} s`;
};
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "");

function RestartSteps({ restart, phase, mode }) {
  const st = restart.status;
  const failedAt = st === "error" ? (restart.sent_at ? 2 : 1) : -1;
  const steps = [
    { label: "Demande envoyée", detail: `${clock(restart.requested_at)}${restart.requested_by_name ? ` · par ${restart.requested_by_name}` : ""}`, done: true },
    { label: mode === "bot" ? "Reçue par le bot" : "Reçue par l'hébergeur", detail: clock(restart.sent_at), done: !!restart.sent_at },
    { label: "Démarrage du bot", detail: st === "sent" ? (phase === "starting" ? "connexion à Discord…" : "arrêt puis redémarrage…") : "", done: st === "done" },
    { label: "En ligne", detail: clock(restart.done_at), done: st === "done" },
  ];
  const active = st === "pending" ? 1 : st === "sent" ? 2 : -1;
  return (
    <ol className="space-y-2.5" data-testid="restart-steps">
      {steps.map((step, i) => {
        const failed = i === failedAt;
        const state = failed ? "error" : step.done && !(st === "error" && i >= failedAt) ? "done" : i === active ? "active" : "waiting";
        return (
          <li key={step.label} className="flex items-center gap-3 text-sm" data-testid={`restart-step-${i}`} data-state={state}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border ${
              state === "done" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                : state === "error" ? "bg-red-500/15 border-red-500/40 text-red-300"
                  : state === "active" ? "bg-sky-500/15 border-sky-500/40 text-sky-300" : "bg-white/5 border-white/10 text-white/30"}`}>
              {state === "done" ? <Check className="w-3.5 h-3.5" /> : state === "error" ? <X className="w-3.5 h-3.5" /> : state === "active" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[11px]">{i + 1}</span>}
            </span>
            <span className={state === "waiting" ? "text-white/40" : "text-white"}>{step.label}</span>
            {step.detail && state !== "waiting" && <span className="text-xs text-white/40 truncate">{step.detail}</span>}
          </li>
        );
      })}
    </ol>
  );
}

function SystemTab({ botId }) {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const last = useRef(null);
  const load = useCallback(() => dash.get(`/bots/${botId}/system`).then((v) => {
    const prev = last.current;
    if (prev?.restart?.id === v.restart?.id && ["pending", "sent"].includes(prev?.restart?.status)) {
      if (v.restart?.status === "done") toast.success("Bot relancé : il est de nouveau en ligne");
      if (v.restart?.status === "error") toast.error("La relance a échoué");
    }
    last.current = v;
    setView(v);
  }).catch(() => {}), [botId]);
  useEffect(() => { load(); }, [load]);
  const restarting = view?.status === "restarting" || view?.status === "starting";
  useEffect(() => {
    const t = setInterval(load, restarting ? 2500 : 15000);
    return () => clearInterval(t);
  }, [load, restarting]);
  // Durées affichées (« en ligne depuis ») mises à jour chaque seconde.
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(t); }, []);

  if (!view) return <p className="text-sm text-text-muted">Chargement…</p>;
  const info = SYSTEM_STATUS[view.status] || SYSTEM_STATUS.offline;
  const r = view.restart;
  const inProgress = r && ["pending", "sent"].includes(r.status);
  let detail = info.text;
  if (view.status === "offline") {
    detail = view.mode === "launcher" && view.launcher?.fresh
      ? "Le bot est coupé de Discord (reconnexion en cours, réseau ou token). Relance-le s'il ne revient pas."
      : view.last_sync_at ? `Dernier contact avec le site ${since(view.last_sync_at)}. Relance-le s'il ne revient pas.` : "Le bot ne s'est jamais connecté au site.";
  }
  const restart = async () => {
    if (!window.confirm("Relancer le bot ? Il sera coupé quelques secondes le temps de redémarrer.")) return;
    setBusy(true);
    try {
      const v = await dash.post(`/bots/${botId}/restart`);
      last.current = v;
      setView(v);
      toast.success("Relance demandée");
    } catch (err) {
      toast.error(errorText(err, "Relance impossible"));
    }
    setBusy(false);
  };
  const blocked = !view.can_restart ? "Réservé à l'acheteur et aux buyers du bot."
    : view.status === "expired" ? "Abonnement expiré : renouvelle-le pour relancer le bot."
      : !view.restart_supported ? view.restart_hint : "";

  return (
    <div className="space-y-4">
      <Panel title="Statut du bot" icon={Activity} actions={<button className="text-xs text-white/40 hover:text-white inline-flex items-center gap-1" onClick={load}><RefreshCw className="w-3.5 h-3.5" />Actualiser</button>}>
        <div className="flex items-start gap-3" data-testid="system-status" data-status={view.status}>
          <span className="relative flex w-3 h-3 mt-1.5 shrink-0">
            {(info.spin || view.status === "online") && <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${info.dot}`} />}
            <span className={`relative inline-flex w-3 h-3 rounded-full ${info.dot}`} />
          </span>
          <div className="min-w-0">
            <p className={`text-lg font-semibold flex items-center gap-2 ${info.cls}`}>{info.label}{info.spin && <Loader2 className="w-4 h-4 animate-spin" />}</p>
            <p className="text-sm text-white/50">{detail}</p>
          </div>
        </div>
        {view.error && view.status !== "online" && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-100" data-testid="system-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span className="break-words min-w-0">{view.error}</span>
          </div>
        )}
        {view.token_invalid && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-sm text-amber-100">
            <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />Discord refuse le token du bot : change-le dans les paramètres du bot puis relance-le.
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {[
            ["En ligne depuis", view.status === "online" && view.started_at ? duration(view.started_at) : "—", "system-uptime"],
            ["Dernier contact", since(view.last_sync_at), "system-last-sync"],
            ["Version", view.bot_version || "—", "system-version"],
            ["Serveurs", String(view.guild_count), "system-guilds"],
          ].map(([label, value, id]) => (
            <div key={label} className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-white/35">{label}</p>
              <p className="font-medium truncate" data-testid={id}>{value}</p>
            </div>
          ))}
        </div>
        {view.mode === "launcher" && view.launcher?.at && !view.launcher.fresh && (
          <p className="text-xs text-amber-200/80 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Lanceur des bots injoignable depuis {since(view.launcher.at).replace("il y a ", "")} : l'hébergement est peut-être arrêté.</p>
        )}
      </Panel>

      <Panel title="Relancer le bot" icon={Power}>
        <p className="text-sm text-white/60">
          Le bot ne répond plus, affiche des erreurs ou une commande bloque ? La relance l'arrête puis le redémarre sur l'hébergement.
          Il est coupé quelques secondes ; tes réglages ne changent pas.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Btn variant="primary" onClick={restart} disabled={!!blocked || inProgress || busy} testId="restart-bot">
            {inProgress || busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
            {inProgress ? "Relance en cours…" : "Relancer le bot"}
          </Btn>
          {blocked && <span className="text-xs text-white/45" data-testid="restart-blocked">{blocked}</span>}
        </div>
        {r && (
          <div className="border-t border-white/5 pt-4 space-y-3" data-testid="last-restart" data-status={r.status}>
            <p className="text-xs uppercase tracking-wide text-white/35">{inProgress ? "Relance en cours" : `Dernière relance · ${since(r.requested_at)}`}</p>
            <RestartSteps restart={r} phase={view.restart_phase} mode={view.mode} />
            {r.status === "error" && (
              <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-100" data-testid="restart-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span className="break-words min-w-0">{r.error}</span>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ── Créateur de serveur par IA ──

const CHANNEL_ICONS = { text: "#", voice: "🔊", announcement: "📣", forum: "💬", stage: "🎙️" };

function PlanPreview({ plan }) {
  if (!plan) return <p className="text-sm text-text-muted">Décris ton serveur à l'IA : le plan proposé s'affiche ici.</p>;
  const channels = plan.categories.reduce((n, c) => n + c.channels.length, 0);
  return (
    <div className="space-y-3" data-testid="ai-plan">
      {plan.summary && <p className="text-sm">{plan.summary}</p>}
      <div className="text-xs text-text-muted">{plan.roles.length} rôles · {plan.categories.length} catégories · {channels} salons{plan.server_name ? ` · nom conseillé : ${plan.server_name}` : ""}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-[#2b2d31] rounded p-2 text-[13px] text-[#949ba4] space-y-2 max-h-[420px] overflow-y-auto">
          {plan.categories.map((c, i) => (
            <div key={i}>
              <div className="uppercase text-[11px] font-semibold flex items-center gap-1">⌄ {c.name}{c.visible_to?.length ? " 🔒" : ""}</div>
              {c.channels.map((ch, j) => (
                <div key={j} className="flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-white/5 text-[#b5bac1]">
                  <span className="w-4 text-center">{CHANNEL_ICONS[ch.type] || "#"}</span>
                  <span className="truncate">{ch.name}</span>
                  {(ch.visible_to?.length > 0 || ch.read_only) && <span className="ml-auto text-[10px]">{ch.visible_to?.length ? "🔒" : "👁️"}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="bg-[#2b2d31] rounded p-2 space-y-1 max-h-[420px] overflow-y-auto">
          <div className="uppercase text-[11px] font-semibold text-[#949ba4]">Rôles</div>
          {plan.roles.map((r, i) => (
            <div key={i} className="text-[13px] flex items-center gap-2 px-1">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: r.color || "#99aab5" }} />
              <span className="truncate" style={{ color: r.color || "#dbdee1" }}>{r.name}</span>
              {r.permissions.includes("Administrator") && <span className="ml-auto text-[10px] text-amber-300">admin</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AiBuilderTab({ botId, guildId, canAct }) {
  const [conv, setConv] = useState(null);
  const [credits, setCredits] = useState(null);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wipe, setWipe] = useState(false);
  const [confirm, setConfirm] = useState("");
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const { actions, add } = useActions(botId, guildId);
  const load = useCallback(() => {
    dash.get(`/bots/${botId}/guilds/${guildId}/ai`).then(setConv).catch(() => {});
    dash.get(`/bots/${botId}/ai/credits`).then(setCredits).catch(() => {});
  }, [botId, guildId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [conv?.messages?.length]);
  if (!conv || !credits) return <p className="text-sm text-text-muted">Chargement…</p>;

  const cost = credits.costs.message + credits.costs.image * images.length;
  const attach = async (files) => {
    for (const file of Array.from(files || []).slice(0, 4 - images.length)) {
      if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type) || file.size > 5 * 1024 * 1024) { toast.error("Image PNG/JPEG/GIF/WebP de 5 Mo maximum"); continue; }
      const data = await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file); });
      try { const res = await axios.post(`${API}/upload/image`, { image: data }, auth()); setImages((l) => [...l, res.data.url]); } catch (err) { toast.error(errorText(err, "Envoi impossible")); }
    }
  };
  const send = async () => {
    if (!text.trim() && !images.length) return;
    setBusy(true);
    try {
      const r = await dash.post(`/bots/${botId}/guilds/${guildId}/ai`, { text, images });
      setConv((c) => ({ ...c, messages: [...c.messages, r.user_message, r.message], plan: r.plan }));
      setCredits((c) => ({ ...c, balance: r.balance }));
      setText(""); setImages([]);
    } catch (err) { toast.error(errorText(err, "L'IA n'a pas répondu")); dash.get(`/bots/${botId}/ai/credits`).then(setCredits).catch(() => {}); }
    setBusy(false);
  };
  const apply = async () => {
    if (wipe && !window.confirm("Tous les salons et rôles actuels du serveur seront supprimés avant la construction. Continuer ?")) return;
    try {
      const r = await dash.post(`/bots/${botId}/guilds/${guildId}/ai/apply`, { wipe, confirm });
      add(r.action);
      toast.success("Le bot construit le serveur (quelques minutes)");
    } catch (err) { toast.error(errorText(err, "Impossible d'appliquer")); }
  };
  const reset = async () => {
    if (!window.confirm("Effacer la conversation et le plan ?")) return;
    await dash.del(`/bots/${botId}/guilds/${guildId}/ai`).catch(() => {});
    load();
  };
  const buy = async (pack) => {
    try { const r = await dash.post(`/bots/${botId}/ai/credits/checkout`, { pack, origin_url: window.location.origin }); window.location.assign(r.url); } catch (err) { toast.error(errorText(err, "Paiement indisponible")); }
  };
  const history = actions.filter((a) => a.type === "build_server");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-4 items-start [&>*]:min-w-0">
      <Panel title="Créateur de serveur IA" icon={Wand2} actions={<span className="text-xs px-2 py-1 rounded-lg border border-blue-500/40 bg-blue-500/10 text-blue-200" data-testid="ai-credits"><Coins className="w-3.5 h-3.5 inline mr-1" />{credits.balance} crédits</span>}>
        {!credits.ai_ready && <div className="text-sm text-amber-100 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">L'IA n'est pas encore configurée sur le site.</div>}
        <div className="h-[420px] overflow-y-auto space-y-3 pr-1" data-testid="ai-chat">
          {conv.messages.length === 0 && (
            <div className="text-sm text-text-muted space-y-2">
              <p>Décris le serveur que tu veux, l'IA prépare tout : catégories, salons texte et vocaux avec emojis, rôles, couleurs et permissions.</p>
              <p className="text-xs">Exemples : « un serveur RP FiveM avec légal, illégal, staff et tickets », « une communauté gaming Valorant avec tournois », « le serveur de mon entreprise ». Tu peux joindre des captures d'un serveur que tu aimes.</p>
            </div>
          )}
          {conv.messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-blue-600/30 border border-blue-500/30" : "bg-white/5 border border-white/10"}`}>
                {m.images?.length > 0 && <div className="flex flex-wrap gap-1 mb-1">{m.images.map((u) => <img key={u} src={u} alt="" className="h-16 rounded" />)}</div>}
                {m.text}
                {m.has_plan && <div className="text-[11px] text-blue-300 mt-1">✓ Plan mis à jour (à droite)</div>}
              </div>
            </div>
          ))}
          {busy && <div className="text-sm text-text-muted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />L'IA prépare ton serveur…</div>}
          <div ref={endRef} />
        </div>
        {images.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {images.map((u) => (
              <div key={u} className="relative"><img src={u} alt="" className="h-14 rounded border border-white/10" />
                <button type="button" aria-label="Retirer" onClick={() => setImages((l) => l.filter((x) => x !== u))} className="absolute -top-1.5 -right-1.5 bg-black rounded-full"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <input ref={fileRef} type="file" multiple accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => { attach(e.target.files); e.target.value = ""; }} data-testid="ai-file" />
          <Btn onClick={() => fileRef.current?.click()} disabled={!canAct || busy || images.length >= 4} title="Joindre une image"><ImagePlus className="w-4 h-4" /></Btn>
          <textarea className={`${inputCls} resize-none`} rows={2} value={text} disabled={!canAct || busy} maxLength={4000} data-testid="ai-input"
            placeholder={canAct ? "Décris ton serveur ou ce qu'il faut changer…" : "Réservé à l'acheteur et aux buyers"}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <Btn variant="primary" onClick={send} disabled={!canAct || busy || (!text.trim() && !images.length) || credits.balance < cost} testId="ai-send"><Send className="w-4 h-4" /></Btn>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-text-muted">
          <span>Ce message : {cost} crédits ({credits.costs.message} + {credits.costs.image} par image). Appliquer le plan est gratuit.</span>
          {conv.messages.length > 0 && canAct && <button type="button" className="underline ml-auto" onClick={reset}>Nouvelle conversation</button>}
        </div>
      </Panel>

      <div className="space-y-4">
        <Panel title="Plan du serveur" icon={Server}>
          <PlanPreview plan={conv.plan} />
          {conv.plan && canAct && (
            <div className="space-y-2 border-t border-white/10 pt-3">
              <label className="flex items-center gap-2 text-sm"><Toggle checked={wipe} onChange={setWipe} label="Tout supprimer avant" testId="ai-wipe" />Supprimer d'abord tous les salons et rôles existants</label>
              {wipe && <TextInput value={confirm} onChange={setConfirm} placeholder="Tape SUPPRIMER pour confirmer" testId="ai-confirm" />}
              <Btn variant="primary" onClick={apply} disabled={wipe && confirm !== "SUPPRIMER"} testId="ai-apply"><Wand2 className="w-4 h-4" />Créer ce serveur avec le bot</Btn>
              <p className="text-[11px] text-text-muted">Le bot a besoin des permissions « Gérer les salons » et « Gérer les rôles » (Administrateur pour donner des rôles admin).</p>
            </div>
          )}
          <ActionHistory actions={history} />
          {history.some((a) => a.status === "done" && a.result?.detail) && <p className="text-xs text-emerald-200">{history.find((a) => a.status === "done")?.result?.detail}</p>}
        </Panel>
        <Panel title="Acheter des crédits" icon={Coins}>
          <div className="grid sm:grid-cols-3 gap-2">
            {credits.packs.map((p) => (
              <button key={p.id} type="button" disabled={!canAct} onClick={() => buy(p.id)} data-testid={`ai-pack-${p.id}`}
                className="rounded-lg border border-white/10 hover:border-blue-500/60 p-3 text-left disabled:opacity-40">
                <div className="font-semibold">{p.label}</div>
                <div className="text-sm text-blue-300">{p.price.toFixed(2).replace(".", ",")} €</div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-text-muted">500 crédits sont offerts avec chaque bot. Les crédits restent sur ce bot.</p>
        </Panel>
      </div>
    </div>
  );
}

// ── Accueil d'un serveur : chiffres, modules, derniers événements ──

function StatCard({ icon: Icon, label, value, tone = "blue", hint }) {
  const tones = { blue: "from-blue-500/20 text-blue-300", emerald: "from-emerald-500/20 text-emerald-300", violet: "from-violet-500/20 text-violet-300", amber: "from-amber-500/20 text-amber-300", rose: "from-rose-500/20 text-rose-300" };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0f1019] p-4">
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${tones[tone]} to-transparent blur-xl opacity-70`} />
      <div className="relative flex items-center gap-3">
        <span className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ${tones[tone].split(" ")[1]}`}><Icon className="w-5 h-5" /></span>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">{label}</div>
          <div className="text-xl font-bold leading-tight truncate">{value}</div>
          {hint && <div className="text-[11px] text-text-muted truncate">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function GuildHome({ view, draft, botId, guildId, go, canAct }) {
  const st = draft || view.settings;
  const text = view.channels.filter((c) => c.type === 0 || c.type === 5).length;
  const voice = view.channels.filter((c) => c.type === 2 || c.type === 13).length;
  const secOn = Object.values(st.security?.modules || {}).filter((v) => v !== "off").length;
  const logsOn = Object.values(st.logs || {}).filter(Boolean).length;
  const modules = [
    { id: "join", label: "Arrivées", on: st.join?.enabled, detail: st.join?.enabled ? (st.join.embed?.enabled ? "Message en embed" : "Message texte") : "Désactivées" },
    { id: "leave", label: "Départs", on: st.leave?.enabled, detail: st.leave?.enabled ? "Message de départ actif" : "Désactivés" },
    { id: "security", label: "Sécurité", on: secOn > 0, detail: `${secOn}/${Object.keys(st.security?.modules || {}).length} modules antiraid` },
    { id: "automod", label: "Modération auto", on: st.automod?.antispam?.enabled || st.automod?.badwords?.enabled, detail: [st.automod?.antispam?.enabled && "anti-spam", st.automod?.badwords?.enabled && "mots bannis"].filter(Boolean).join(" · ") || "Désactivée" },
    { id: "netban", label: "Bans réseau", on: st.netban?.enabled, detail: st.netban?.enabled ? [NETBAN_ACTIONS.find((a) => a.value === st.netban.action)?.label, st.netban.action !== "alert" && st.netban.categories && `${st.netban.categories.length} catégorie(s)`].filter(Boolean).join(" · ") : "Désactivés" },
    { id: "logs", label: "Logs", on: true, detail: `Journal du site actif · ${logsOn} salon${logsOn > 1 ? "s" : ""} Discord` },
    { id: "leveling", label: "Niveaux", on: st.leveling?.enabled, detail: st.leveling?.enabled ? `${st.leveling.rewards.length} récompense(s)` : "Désactivés" },
    { id: "tickets", label: "Tickets", on: !!st.tickets?.channelId, detail: st.tickets?.channelId ? `${st.tickets.options.length} type(s) de ticket` : "Pas de panel" },
    { id: "rolemenus", label: "Menus de rôles", on: (st.rolemenus?.menus || []).length > 0, detail: `${(st.rolemenus?.menus || []).length} menu(s)` },
    { id: "autoreact", label: "Réactions auto", on: (st.autoreact?.rules || []).length > 0, detail: `${(st.autoreact?.rules || []).length} règle(s)` },
    { id: "notifications", label: "Notifications", on: (st.notifications?.alerts || []).length > 0, detail: `${(st.notifications?.alerts || []).length} alerte(s)` },
  ];
  const icons = Object.fromEntries(TABS.map((t) => [t.id, t.icon]));
  const [recent, setRecent] = useState(null);
  useEffect(() => {
    const load = () => dash.get(`/bots/${botId}/guilds/${guildId}/logs`).then((r) => setRecent(r.logs.slice(0, 6))).catch(() => setRecent((x) => x || []));
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => clearInterval(t);
  }, [botId, guildId]);
  return (
    <div className="space-y-5" data-testid="guild-home">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Membres" value={view.guild?.memberCount ?? "—"} tone="blue" />
        <StatCard icon={Hash} label="Salons" value={text + voice} hint={`${text} textuels · ${voice} vocaux`} tone="violet" />
        <StatCard icon={Tags} label="Rôles" value={view.roles.filter((r) => !r.everyone).length} tone="amber" />
        <StatCard icon={Activity} label="Bot" value={view.online ? "En ligne" : "Hors ligne"} hint={view.state_at ? `Synchro ${since(view.state_at)}` : "Jamais synchronisé"} tone={view.online ? "emerald" : "rose"} />
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 items-start">
        <Panel title="Modules" icon={LayoutDashboard}>
          <div className="grid sm:grid-cols-2 gap-2 -mt-1">
            {modules.map((m) => {
              const Icon = icons[m.id] || Activity;
              return (
                <button key={m.id} type="button" onClick={() => go(m.id)} data-testid={`home-module-${m.id}`}
                  className="group flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-blue-500/30 px-3 py-2.5 text-left transition-colors">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.on ? "bg-blue-500/15 text-blue-300" : "bg-white/5 text-white/30"}`}><Icon className="w-4 h-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-sm font-medium">{m.label}<span className={`w-1.5 h-1.5 rounded-full ${m.on ? "bg-emerald-400" : "bg-white/20"}`} /></span>
                    <span className="block text-[11px] text-text-muted truncate">{m.detail}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/60 shrink-0" />
                </button>
              );
            })}
          </div>
        </Panel>
        <div className="space-y-4">
          <Panel title="Raccourcis" icon={Sparkles}>
            <div className="grid grid-cols-2 gap-2 -mt-1">
              {[["embeds", "Envoyer un embed"], ["tickets", "Panel tickets"], ["ai", "Créateur IA"], ["system", "Statut du bot"]].map(([id, label]) => {
                const Icon = icons[id] || Activity;
                return (
                  <button key={id} type="button" onClick={() => go(id)} disabled={!canAct && id !== "system"}
                    className="flex flex-col items-start gap-2 rounded-xl border border-white/5 bg-gradient-to-br from-white/[0.04] to-transparent hover:border-blue-500/30 p-3 text-left text-sm disabled:opacity-40">
                    <Icon className="w-4 h-4 text-blue-300" />{label}
                  </button>
                );
              })}
            </div>
          </Panel>
          <Panel title="Derniers événements" icon={Activity} actions={<button type="button" className="text-xs text-blue-300 hover:underline" onClick={() => go("logs")}>Tout voir</button>}>
            <div className="-mx-4 -mb-4 -mt-4 divide-y divide-white/5" data-testid="home-recent">
              {!recent && <div className="p-4 text-xs text-text-muted">Chargement…</div>}
              {recent && recent.length === 0 && <div className="p-4 text-xs text-text-muted">Rien pour l'instant : les arrivées, départs, messages supprimés… apparaîtront ici.</div>}
              {recent && recent.map((e) => <JournalRow key={e.id} e={e} />)}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

// ── Pages ──

function Frame({ children }) {
  return <div className="min-h-screen bg-[#0a0a0f] text-white"><Navbar />{children}</div>;
}

function RoleBadge({ role, compact }) {
  const info = ROLE_INFO[role];
  if (!info) return null;
  const Icon = info.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${compact ? "px-1.5 py-0.5 text-[11px]" : "px-3 py-1.5 text-sm"} border ${info.cls}`} data-testid="dash-role">
      <Icon className={compact ? "w-3 h-3" : "w-4 h-4"} />{info.label}
    </span>
  );
}

function OnlineBadge({ bot }) {
  return (
    <span data-testid="bot-online" className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm border ${bot.online ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/5 text-text-muted"}`}>
      <span className={`w-2 h-2 rounded-full ${bot.online ? "bg-emerald-400" : "bg-white/30"}`} />
      {bot.online ? "Bot connecté au site" : bot.last_sync_at ? `Hors ligne (vu ${since(bot.last_sync_at)})` : "Jamais connecté au site"}
    </span>
  );
}

function GuildIcon({ g, size = "w-12 h-12", small }) {
  const radius = small ? "rounded-lg" : "rounded-2xl";
  return g.icon_url
    ? <img src={g.icon_url} alt="" className={`${size} ${radius} shrink-0 object-cover`} />
    : <div className={`${size} ${radius} shrink-0 bg-gradient-to-br from-[#26305c] to-[#1a1f3d] flex items-center justify-center font-semibold text-blue-200 ${small ? "text-[10px]" : ""}`}>{(g.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 3)}</div>;
}

// Choix du serveur dans le menu latéral : liste avec icônes, recherche si beaucoup de serveurs.
function GuildSwitcher({ botId, guilds, current, confirmLeave }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  const go = (path) => { setOpen(false); if (confirmLeave()) navigate(path); };
  const g = guilds.find((x) => x.id === current) || { id: current, name: "Serveur" };
  const q = query.trim().toLowerCase();
  const list = q ? guilds.filter((x) => (x.name || "").toLowerCase().includes(q)) : guilds;
  const members = (x) => (x.memberCount ? `${x.memberCount.toLocaleString("fr-FR")} membres` : "Serveur Discord");
  return (
    <div className="relative" ref={ref}>
      <div className="text-[11px] uppercase tracking-wide text-white/30 mb-1.5">Serveur</div>
      <button type="button" data-testid="guild-switch" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-colors ${open ? "border-blue-500/60 bg-blue-500/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
        <GuildIcon g={g} size="w-8 h-8" small />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{g.name}</p>
          <p className="text-[11px] text-white/40 truncate">{members(g)}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="listbox" data-testid="guild-switch-list"
          className="absolute left-0 right-0 mt-1.5 z-30 rounded-xl border border-white/10 bg-[#12121a] shadow-2xl shadow-black/60 overflow-hidden">
          {guilds.length > 6 && (
            <div className="p-2 border-b border-white/5">
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un serveur"
                className="w-full bg-black/30 rounded-lg px-2.5 py-1.5 text-sm placeholder:text-white/30 focus:outline-none" />
            </div>
          )}
          <div className="max-h-72 overflow-y-auto p-1.5 space-y-0.5">
            {list.length === 0 && <p className="px-2.5 py-2 text-xs text-white/40">Aucun serveur</p>}
            {list.map((x) => {
              const on = x.id === current;
              return (
                <button key={x.id} type="button" role="option" aria-selected={on} data-testid={`guild-option-${x.id}`}
                  onClick={() => (on ? setOpen(false) : go(`/bot-dashboard/${botId}/${x.id}`))}
                  className={`w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors ${on ? "bg-blue-500/15" : "hover:bg-white/[0.06]"}`}>
                  <GuildIcon g={x} size="w-7 h-7" small />
                  <div className="min-w-0 flex-1">
                    <p className={`text-[13px] truncate ${on ? "text-white font-medium" : "text-white/80"}`}>{x.name}</p>
                    <p className="text-[10px] text-white/35 truncate">{members(x)}{x.pending ? " · en attente" : ""}</p>
                  </div>
                  {on && <Check className="w-4 h-4 text-blue-400 shrink-0" />}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => go(`/bot-dashboard/${botId}`)} data-testid="guild-switch-all"
            className="w-full flex items-center gap-2 border-t border-white/5 px-3.5 py-2.5 text-xs text-white/50 hover:text-white hover:bg-white/[0.04]">
            <Settings2 className="w-3.5 h-3.5" />Tous les serveurs
          </button>
        </div>
      )}
    </div>
  );
}

function BotHeader({ info, guild }) {
  const b = info.bot;
  const role = ROLE_INFO[b.role];
  return (
    <>
      <div className="relative overflow-hidden flex flex-wrap items-center gap-4 mb-4 rounded-2xl border border-white/10 bg-[#101019] p-4">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/15 via-indigo-600/5 to-transparent pointer-events-none" />
        {guild ? <div className="relative"><GuildIcon g={guild} size="w-12 h-12" /></div>
          : b.bot_avatar_url ? <img src={b.bot_avatar_url} alt="" className="relative w-12 h-12 rounded-xl" /> : <div className="relative w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center"><Bot className="w-6 h-6 text-blue-300" /></div>}
        <div className="relative flex-1 min-w-[200px]">
          <h1 className="font-heading text-xl sm:text-2xl font-bold truncate">{guild ? guild.name : b.bot_name}</h1>
          <p className="text-sm text-text-muted truncate">{guild ? `avec ${b.bot_name}` : b.bot_username ? `${b.bot_username} sur Discord` : b.kind === "radio" ? "Bot radio" : "Bot de gestion"}{b.bot_version ? ` · v${b.bot_version}` : ""}</p>
        </div>
        <div className="relative flex flex-wrap gap-2"><RoleBadge role={b.role} /><OnlineBadge bot={b} /></div>
        <p className="relative w-full text-xs text-text-muted" data-testid="dash-rights">{role?.rights}</p>
      </div>
      {b.token_invalid && (
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-100 mb-4"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Discord refuse le token de ce bot : il ne peut pas se connecter.{b.role === "acheteur" && <a href="#bot-settings" className="underline ml-1">Changer le token</a>}</div>
      )}
      {!b.online && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-100 mb-4" data-testid="offline-hint">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />Le bot n'est pas connecté au tableau de bord : tes changements sont gardés et appliqués dès qu'il se connecte (bot démarré, à jour, avec la clé API du site).
        </div>
      )}
    </>
  );
}

// Réglages du bot réservés à l'acheteur : nom, token Discord, actualisation depuis Discord.
function BotSettingsPanel({ botId, bot, onChanged }) {
  const [name, setName] = useState(bot.bot_name || "");
  const [token, setToken] = useState("");
  const [nextChange, setNextChange] = useState(null);
  const [changesLeft, setChangesLeft] = useState(null);
  const [tokenRefused, setTokenRefused] = useState(false);
  const [switchKey, setSwitchKey] = useState(0);
  const [busy, setBusy] = useState("");
  const details = useCallback((refresh) => axios.get(`${API}/bots/${botId}/details${refresh ? "?refresh=true" : ""}`, auth())
    .then((r) => {
      setNextChange(r.data.bot?.token_change_available_at ? new Date(r.data.bot.token_change_available_at) : null);
      setChangesLeft(r.data.bot?.token_changes_left ?? null);
      setTokenRefused(Boolean(r.data.token_invalid));
    }), [botId]);
  useEffect(() => { details(false).catch(() => {}); }, [details]);
  useEffect(() => { setName(bot.bot_name || ""); }, [bot.bot_name]);
  // Token refusé par Discord : on peut toujours le remplacer, même après 2 changements.
  const tokenLocked = !!nextChange && nextChange > new Date() && !tokenRefused;
  const run = async (key, fn, okText, failText) => {
    setBusy(key);
    try { await fn(); toast.success(okText); onChanged(); } catch (err) { toast.error(errorText(err, failText)); } finally { setBusy(""); }
  };
  const rename = () => {
    const clean = name.trim().replace(/\s+/g, " ");
    if (clean.length < 2 || clean.length > 32) { toast.error("Le nom doit faire entre 2 et 32 caractères"); return; }
    run("name", () => axios.put(`${API}/bots/${botId}/name`, { bot_name: clean }, auth()), "Nom du bot modifié", "Impossible de modifier le nom");
  };
  const changeToken = () => run("token", async () => {
    await axios.put(`${API}/bots/${botId}/token`, { discord_token: token.trim() }, auth());
    setToken("");
    setSwitchKey((k) => k + 1);
    await details(false).catch(() => {});
  }, "Token changé : le bot redémarre avec le nouveau token", "Impossible de changer le token");
  const refresh = () => run("refresh", () => details(true), "Logo et serveurs actualisés depuis Discord", "Actualisation impossible, réessaie dans quelques secondes");

  return (
    <Panel title="Réglages du bot" icon={Settings2} actions={
      <Btn onClick={refresh} disabled={busy === "refresh"} testId="bot-refresh" title="Relire le logo, le nom Discord et les serveurs">
        <RefreshCw className={`w-4 h-4 ${busy === "refresh" ? "animate-spin" : ""}`} /><span className="hidden sm:inline">Actualiser depuis Discord</span>
      </Btn>
    }>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg bg-black/20 border border-white/5 p-3 space-y-2">
          <div className="text-sm font-medium flex items-center gap-2"><Pencil className="w-4 h-4 text-blue-400" />Nom du bot</div>
          <p className="text-xs text-text-muted">Le nom affiché sur le site (2 à 32 caractères).</p>
          <div className="flex gap-2">
            <TextInput value={name} onChange={setName} maxLength={32} testId="bot-name-input" />
            <Btn variant="primary" onClick={rename} disabled={busy === "name" || name.trim() === bot.bot_name} testId="bot-name-save"><Save className="w-4 h-4" />Enregistrer</Btn>
          </div>
        </div>
        <div className="rounded-lg bg-black/20 border border-white/5 p-3 space-y-2">
          <div className="text-sm font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-blue-400" />Token Discord</div>
          <p className="text-xs text-text-muted">2 fois par jour. Il est vérifié auprès de Discord, puis le bot redémarre avec lui et se reconnecte au site.</p>
          <p className="text-[11px] text-text-muted">Active d'abord les 3 « Privileged Gateway Intents » (Presence, Server Members, Message Content) dans le Developer Portal &gt; Bot.</p>
          {tokenRefused && (
            <p className="text-xs text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="bot-token-refused">
              Discord refuse le token actuel (il a été regénéré ou révoqué) : colle le token actuel du Developer Portal &gt; Bot.
            </p>
          )}
          {tokenLocked ? (
            <p className="text-xs text-text-secondary bg-black/30 rounded-lg px-3 py-2" data-testid="bot-token-locked">
              Déjà changé 2 fois en 24 h : prochain changement le <span className="text-white">{nextChange.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} à {nextChange.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>.
            </p>
          ) : (
            <div className="flex gap-2">
              <TextInput type="password" value={token} onChange={setToken} placeholder="Developer Portal > Bot > Reset Token" testId="bot-token-input" />
              <Btn variant="primary" onClick={changeToken} disabled={busy === "token" || !token.trim()} testId="bot-token-save">
                {busy === "token" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Changer"}
              </Btn>
            </div>
          )}
          {!tokenLocked && changesLeft === 1 && <p className="text-[11px] text-text-muted" data-testid="bot-token-left">Encore 1 changement possible aujourd'hui.</p>}
          <TokenSwitchStatus botId={botId} refreshKey={switchKey} />
        </div>
      </div>
    </Panel>
  );
}

function BotHero({ info }) {
  const b = info.bot;
  const members = info.guilds.reduce((n, g) => n + (g.memberCount || 0), 0);
  const expires = b.expires_at ? new Date(b.expires_at) : null;
  const stats = [
    { label: "Serveurs", value: info.guilds.length, icon: Server },
    { label: "Membres", value: members ? members.toLocaleString("fr-FR") : "—", icon: Users },
    { label: "Version", value: b.bot_version ? `v${b.bot_version}` : "—", icon: Cpu },
    { label: "Abonnement", value: expires ? (expires.getFullYear() > 2090 ? "À vie" : `jusqu'au ${expires.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`) : "—", icon: Clock },
  ];
  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#101019] mb-6" data-testid="bot-hero">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/25 via-indigo-600/10 to-transparent pointer-events-none" />
      <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
      {b.bot_avatar_url && <img src={b.bot_avatar_url} alt="" className="absolute -right-10 -bottom-16 w-64 h-64 rounded-full opacity-[0.07] blur-sm pointer-events-none" />}
      <div className="relative p-5 sm:p-7">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative shrink-0">
            {b.bot_avatar_url ? <img src={b.bot_avatar_url} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl ring-2 ring-white/10 shadow-xl shadow-blue-900/30" />
              : <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xl"><Bot className="w-8 h-8 text-white" /></div>}
            <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 border-[#101019] ${b.online ? "bg-emerald-400" : "bg-white/30"}`} title={b.online ? "En ligne" : "Hors ligne"} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{b.bot_name}</h1>
            <p className="text-sm text-white/50">{b.bot_username ? `${b.bot_username} sur Discord` : b.kind === "radio" ? "Bot radio" : "Bot de gestion"}</p>
          </div>
          <div className="flex flex-wrap gap-2"><RoleBadge role={b.role} /><OnlineBadge bot={b} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-6">
          {stats.map(({ label, value, icon: Icon }) => (
            <div key={label} className="rounded-xl bg-black/25 border border-white/5 px-3 py-2.5 flex items-center gap-2.5">
              <Icon className="w-4 h-4 text-blue-300 shrink-0" />
              <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div><div className="text-sm font-semibold truncate">{value}</div></div>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/40 mt-4" data-testid="dash-rights">{ROLE_INFO[b.role]?.rights}</p>
      </div>
    </section>
  );
}

function QuickCard({ to, href, icon: Icon, title, text, tone, testId }) {
  const tones = { indigo: "from-indigo-500/20 text-indigo-300 hover:border-indigo-400/50", blue: "from-blue-500/20 text-blue-300 hover:border-blue-400/50", violet: "from-violet-500/20 text-violet-300 hover:border-violet-400/50" };
  const cls = `group relative overflow-hidden flex items-start gap-3 rounded-xl border border-white/5 bg-gradient-to-br ${tones[tone]} to-[#12121a] p-4 transition-all hover:-translate-y-0.5`;
  const body = (
    <>
      <span className="w-10 h-10 rounded-xl bg-black/30 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
      <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-white">{title}</span><span className="block text-xs text-white/50 mt-0.5">{text}</span></span>
      <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/70 mt-1 shrink-0" />
    </>
  );
  return to ? <Link to={to} className={cls} data-testid={testId}>{body}</Link> : <a href={href} className={cls} data-testid={testId}>{body}</a>;
}

function GuildCard({ g, botId }) {
  return (
    <Link to={`/bot-dashboard/${botId}/${g.id}`} data-testid={`dash-guild-${g.id}`}
      className="group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0f1019] hover:border-blue-500/40 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-900/20">
      <div className="relative h-20 overflow-hidden">
        {g.icon_url ? <img src={g.icon_url} alt="" className="absolute inset-0 w-full h-full object-cover scale-150 blur-2xl opacity-50" />
          : <div className="absolute inset-0 bg-gradient-to-br from-blue-600/40 via-indigo-600/20 to-transparent" />}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#12121a]" />
      </div>
      <div className="relative px-4 pb-4 -mt-9">
        <div className="ring-4 ring-[#12121a] rounded-2xl w-fit"><GuildIcon g={g} size="w-14 h-14" /></div>
        <div className="mt-2 font-semibold truncate">{g.name}</div>
        <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
          <Users className="w-3.5 h-3.5" />{g.memberCount ? `${g.memberCount.toLocaleString("fr-FR")} membres` : "Serveur"}
          {g.pending && <span className="ml-auto text-amber-300">changements en attente</span>}
        </div>
        <div className="mt-3 inline-flex items-center gap-1 text-xs text-blue-300 group-hover:gap-2 transition-all">Gérer le serveur<ChevronRight className="w-3.5 h-3.5" /></div>
      </div>
    </Link>
  );
}

function GuildPicker({ botId }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const reload = useCallback(() => dash.get(`/bots/${botId}`).then(setInfo).catch((err) => setError(errorText(err, "Bot introuvable"))), [botId]);
  useEffect(() => { reload(); }, [reload]);
  if (error) return <main className="max-w-3xl mx-auto px-4 pt-28"><Panel><p className="text-sm">{error}. <Link to="/dashboard" className="underline">Retour</Link></p></Panel></main>;
  if (!info) return <main className="max-w-3xl mx-auto px-4 pt-28 text-sm text-text-muted"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</main>;
  const b = info.bot;
  const full = ["acheteur", "buyer"].includes(b.role);
  const guilds = info.guilds.filter((g) => !q.trim() || g.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <main className="max-w-6xl mx-auto px-4 pt-24 pb-16">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Tableau de bord</Link>
      <BotHero info={info} />
      {info.admin_access && (
        <div className="flex items-start gap-2 rounded-xl bg-violet-500/10 border border-violet-500/30 px-4 py-3 text-sm text-violet-100 mb-4" data-testid="admin-access-hint">
          <Shield className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Accès administrateur : tu gères ce bot en tant qu'administrateur du site, avec les mêmes droits que son acheteur. <Link to="/admin?tab=all-bots" className="underline">Tous les bots</Link></span>
        </div>
      )}
      {b.token_invalid && (
        <div className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-100 mb-4"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Discord refuse le token de ce bot : il ne peut pas se connecter.{b.role === "acheteur" && <a href="#bot-settings" className="underline ml-1">Changer le token</a>}</div>
      )}
      {!b.online && (
        <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-100 mb-4" data-testid="offline-hint">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />Le bot n'est pas connecté au tableau de bord : tes changements sont gardés et appliqués dès qu'il se connecte (bot démarré, à jour, avec la clé API du site).
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {full && b.kind !== "radio" && <QuickCard to={`/bot-console/${botId}`} icon={Terminal} tone="indigo" title="Console Discord" testId="open-bot-console"
          text="Lis les salons, écris, réponds et envoie des MP avec le compte du bot." />}
        {b.role === "acheteur" && <QuickCard href="#bot-settings" icon={Settings2} tone="blue" title="Réglages du bot" text="Nom, token Discord, logo et serveurs." />}
        <QuickCard href="#bot-team" icon={UserCog} tone="violet" title="Équipe du bot" text="Buyers et owners qui gèrent le bot avec toi." />
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Server className="w-5 h-5 text-blue-400" />Serveurs</h2>
          <p className="text-sm text-text-muted">Choisis un serveur pour régler le bot dessus.</p>
        </div>
        {info.guilds.length > 6 && (
          <label className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-text-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un serveur" className={`${inputCls} pl-8`} />
          </label>
        )}
      </div>
      {info.guilds.length === 0 ? (
        <Panel><p className="text-sm">Le bot n'est sur aucun serveur pour l'instant. Invite-le sur ton serveur depuis le Discord Developer Portal (OAuth2 &gt; URL Generator, scope « bot »).</p></Panel>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {guilds.map((g) => <GuildCard key={g.id} g={g} botId={botId} />)}
          {guilds.length === 0 && <p className="text-sm text-text-muted">Aucun serveur ne correspond.</p>}
        </div>
      )}
      {b.role === "acheteur" && (
        <section id="bot-settings" className="mt-10 scroll-mt-24" data-testid="bot-settings">
          <BotSettingsPanel botId={botId} bot={b} onChanged={reload} />
        </section>
      )}
      <section id="bot-team" className="mt-10 scroll-mt-24" data-testid="bot-team">
        <h2 className="font-heading text-xl font-semibold mb-3 flex items-center gap-2"><UserCog className="w-5 h-5 text-blue-400" />Équipe du bot</h2>
        <TeamTab botId={botId} />
      </section>
    </main>
  );
}

function GuildConfig({ botId, guildId }) {
  const [info, setInfo] = useState(null);
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState(null);
  const [wantedTab, setTab] = useState("home");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const savedRef = useRef(null);

  const pendingRef = useRef([]);
  const applyView = useCallback((v, keepDraft, fromPoll) => {
    if (fromPoll && savedRef.current) {
      // Réglage changé avec une commande sur Discord (pas par ce site) : on le signale.
      const changed = Object.keys(v.settings).filter((k) => !pendingRef.current.includes(k) && !v.pending.includes(k)
        && JSON.stringify(v.settings[k]) !== JSON.stringify(savedRef.current[k]));
      if (changed.length) toast.info(`Mis à jour depuis Discord : ${changed.map((k) => SECTION_LABELS[k] || k).join(", ")}`);
    }
    setView(v);
    savedRef.current = v.settings;
    pendingRef.current = v.pending;
    setDraft((d) => {
      if (!keepDraft || !d) return clone(v.settings);
      // Les sections non modifiées suivent le site (état du bot) ; les autres gardent la saisie.
      const out = {};
      Object.keys(v.settings).forEach((k) => { out[k] = JSON.stringify(d[k]) !== JSON.stringify(keepDraft[k]) ? d[k] : clone(v.settings[k]); });
      return out;
    });
  }, []);

  const load = useCallback((keep, fromPoll) => dash.get(`/bots/${botId}/guilds/${guildId}`).then((v) => applyView(v, keep, fromPoll))
    .catch((err) => { if (!fromPoll) setError(errorText(err, "Serveur introuvable")); }), [botId, guildId, applyView]);
  useEffect(() => { dash.get(`/bots/${botId}`).then(setInfo).catch((err) => setError(errorText(err, "Bot introuvable"))); load(null); }, [botId, load]);

  // L'état est relu toutes les 4 s tant que des changements attendent le bot, sinon
  // toutes les 8 s (onglet visible) : ce qui est réglé sur Discord apparaît ici.
  // Les sections en cours de modification gardent la saisie.
  const waiting = view && view.pending.length > 0;
  useEffect(() => {
    const t = setInterval(() => { if (waiting || !document.hidden) load(savedRef.current, true); }, waiting ? 4000 : 8000);
    return () => clearInterval(t);
  }, [waiting, load]);

  const dirtySections = useMemo(() => {
    if (!view || !draft) return [];
    return Object.keys(draft).filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(view.settings[k]));
  }, [view, draft]);
  useEffect(() => {
    const warn = (e) => { if (dirtySections.length) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtySections]);

  if (error) return <Frame><main className="max-w-3xl mx-auto px-4 pt-28"><Panel><p className="text-sm">{error}. <Link to={`/bot-dashboard/${botId}`} className="underline">Retour</Link></p></Panel></main></Frame>;
  if (!info || !view || !draft) return <Frame><main className="max-w-3xl mx-auto px-4 pt-28 text-sm text-white/40">Chargement…</main></Frame>;

  const editable = view.permissions.edit_sections;
  const save = async () => {
    setSaving(true);
    let last = null;
    for (const section of dirtySections) {
      try {
        last = await dash.put(`/bots/${botId}/guilds/${guildId}/${section}`, { values: draft[section] });
      } catch (err) {
        toast.error(`${SECTION_LABELS[section]} : ${errorText(err, "enregistrement impossible")}`);
        setTab(section);
        setSaving(false);
        if (last) applyView({ ...view, ...last }, savedRef.current);
        return;
      }
    }
    if (last) applyView({ ...view, ...last }, null);
    toast.success(view.online ? "Enregistré : le bot applique les changements" : "Enregistré : appliqué à la prochaine connexion du bot");
    setSaving(false);
  };
  const cancelPending = async (section) => {
    try { const v = await dash.del(`/bots/${botId}/guilds/${guildId}/${section}/pending`); applyView({ ...view, ...v }, null); toast.success("Changement annulé"); } catch (err) { toast.error(errorText(err, "Erreur")); }
  };

  const props = (section) => ({
    s: draft[section],
    set: (v) => setDraft({ ...draft, [section]: v }),
    ro: !editable.includes(section),
    channels: view.channels,
    roles: view.roles,
    meta: view.meta,
  });
  const kindTabs = info.bot.kind === "radio" ? RADIO_TABS : TABS;
  // Onglet ouvert : le premier du bot si celui demandé n'existe pas pour ce type de bot.
  const tab = kindTabs.some((t) => t.id === wantedTab) ? wantedTab : kindTabs[0].id;
  const isSection = !NON_SECTION_TABS.includes(tab);
  const canAct = view.permissions.actions ?? view.permissions.edit_sections.includes("join");

  const b = { ...info.bot, online: view.online };
  const sections = kindTabs.map((t) => ({
    ...t,
    dot: view.errors[t.id] ? "red" : dirtySections.includes(t.id) ? "blue" : view.pending.includes(t.id) ? "amber" : null,
  }));
  const brand = (
    <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2.5">
      {b.bot_avatar_url ? <img src={b.bot_avatar_url} alt="" className="w-8 h-8 rounded-lg" /> : <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-white" /></div>}
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight truncate">{b.bot_name}</p>
        <p className="text-[11px] text-white/40 truncate">{ROLE_INFO[b.role]?.label} · {b.online ? "en ligne" : "hors ligne"}</p>
      </div>
    </div>
  );
  const guildNav = (
    <GuildSwitcher botId={botId} guilds={info.guilds} current={guildId}
      confirmLeave={() => !dirtySections.length || window.confirm("Changements non enregistrés : changer quand même de serveur ?")} />
  );
  const header = (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-white/45">
        <Link to={`/bot-dashboard/${botId}`} className="inline-flex items-center gap-1.5 hover:text-white"><ArrowLeft className="w-4 h-4" />Serveurs du bot</Link>
        <button className="inline-flex items-center gap-1 hover:text-white ml-auto" onClick={() => load(savedRef.current)}><RefreshCw className="w-4 h-4" />Actualiser</button>
      </div>
      <BotHeader info={{ ...info, bot: b }} guild={view.guild} />
      {!view.discord_ok && (
        <div className="flex items-start gap-2 bg-sky-500/10 border border-sky-500/30 rounded-lg px-3 py-2 text-sm text-sky-100 mb-4"><Hash className="w-4 h-4 mt-0.5 shrink-0" />Discord n'a pas donné la liste des salons et des rôles : saisis leurs identifiants à la main.</div>
      )}
    </>
  );

  return (
    <PanelShell brand={brand} sections={sections} active={tab} onSelect={setTab} back={{ to: "/dashboard", label: "Tableau de bord" }}
      extraNav={guildNav} header={header} testid="dash">
      {isSection && (
        <div className="space-y-3 mb-4">
          {!editable.includes(tab) && (
            <div className="flex items-start gap-2 bg-white/5 border border-white/5 rounded-lg px-3 py-2 text-sm text-white/50" data-testid="readonly-hint"><Shield className="w-4 h-4 mt-0.5 shrink-0" />Lecture seule : réservé à l'acheteur et aux buyers du bot.</div>
          )}
          <SectionStatus pending={view.pending.includes(tab)} error={view.errors[tab]} online={view.online} />
          {view.pending.includes(tab) && editable.includes(tab) && (
            <button className="text-xs underline text-white/50 hover:text-white" onClick={() => cancelPending(tab)}>Annuler ce changement</button>
          )}
        </div>
      )}

      {tab === "home" && <GuildHome view={view} draft={draft} botId={botId} guildId={guildId} go={setTab} canAct={canAct} />}
      {tab === "general" && <GeneralTab {...props("general")} />}
      {tab === "join" && <JoinTab {...props("join")} />}
      {tab === "leave" && <LeaveTab {...props("leave")} />}
      {tab === "security" && <SecurityTab {...props("security")} />}
      {tab === "automod" && <AutomodTab {...props("automod")} />}
      {tab === "netban" && <NetbanTab {...props("netban")} />}
      {tab === "logs" && <LogsTab {...props("logs")} botId={botId} guildId={guildId} />}
      {tab === "leveling" && <LevelingTab {...props("leveling")} />}
      {tab === "notifications" && <NotificationsTab {...props("notifications")} />}
      {tab === "tickets" && <TicketsTab {...props("tickets")} botId={botId} guildId={guildId} dirty={dirtySections.includes("tickets")} canAct={canAct} />}
      {tab === "rolemenus" && <RoleMenusTab {...props("rolemenus")} botId={botId} guildId={guildId} dirty={dirtySections.includes("rolemenus")} canAct={canAct} />}
      {tab === "autoreact" && <AutoreactTab {...props("autoreact")} />}
      {tab === "ai" && <AiBuilderTab botId={botId} guildId={guildId} canAct={canAct} />}
      {tab === "embeds" && <EmbedsTab botId={botId} guildId={guildId} channels={view.channels} canAct={canAct} />}
      {tab === "radio" && <RadioTab {...props("radio")} botId={botId} guildId={guildId} dirty={dirtySections.includes("radio")} canAct={canAct} online={view.online} initialLive={view.live} />}
      {tab === "bot" && <BotSettingsTab botId={botId} />}
      {tab === "commands" && <CommandsTab botId={botId} go={setTab} />}
      {tab === "team" && <TeamTab botId={botId} />}
      {tab === "system" && <SystemTab botId={botId} />}

      {view.state_at && isSection && <p className="text-[11px] text-white/30 mt-4">Réglages lus sur le bot {since(view.state_at)}.</p>}

      {dirtySections.length > 0 && (
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-40 bg-[#0c0c14]/95 backdrop-blur border-t border-blue-500/30" data-testid="save-bar">
          <div className="max-w-6xl px-4 lg:px-8 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm flex-1 min-w-[200px]">Modifié : {dirtySections.map((x) => SECTION_LABELS[x]).join(", ")}</span>
            <Btn onClick={() => setDraft(clone(view.settings))}>Annuler</Btn>
            <Btn variant="primary" onClick={save} disabled={saving} data-testid="save-dash">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}{saving ? "Enregistrement…" : "Enregistrer et appliquer"}</Btn>
          </div>
        </div>
      )}
    </PanelShell>
  );
}

export default function BotDashboardPage() {
  const { botId, guildId } = useParams();
  // Retour du paiement Stripe d'un pack de crédits IA.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("credits_session");
    if (!sid) return;
    window.history.replaceState(null, "", window.location.pathname);
    axios.get(`${API}/checkout/status/${sid}`, auth())
      .then((r) => (r.data.payment_status === "paid" || r.data.status === "complete" ? toast.success("Crédits ajoutés à ton bot") : toast.info("Paiement en cours de validation")))
      .catch(() => toast.error("Paiement non confirmé"));
  }, []);
  // L'ancienne liste des bots est remplacée par le tableau de bord du compte.
  if (!botId) return <Navigate to="/dashboard" replace />;
  if (guildId) return <GuildConfig key={`${botId}-${guildId}`} botId={botId} guildId={guildId} />;
  return (
    <Frame>
      <GuildPicker botId={botId} />
    </Frame>
  );
}
